/*
=== ENVIRONMENT CONFIGURATION ===
To switch between development and production:

1. API ENDPOINTS:
   - DEV: Use 'http://localhost:8000/api/start-interview'
   - PROD: Use '/api/start-interview'

2. WEBSOCKET CONNECTION:
   - DEV: Use 'ws://localhost:8000/ws/listening'
   - PROD: Use dynamic protocol/host detection

3. AUDIO URL FIXING:
   - DEV: Use 'http://localhost:8000${audioUrl}'
   - PROD: Use audioUrl as-is (relative URLs)

Instructions:
- For DEVELOPMENT: Keep dev lines uncommented, comment out prod lines
- For PRODUCTION: Comment out dev lines, uncomment prod lines
- Look for "=== DEV/PROD SETTINGS ===" comments below
*/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  API_CONFIG,
  INTERVIEW_CONFIG,
  createAuthenticatedWebSocket,
  fetchAuthenticatedAudio,
} from '../../../utils/constants';
import { interviewApi } from '../../../api/interviewApi';
import { settingsApi } from '../../../api/settingsApi';
import { buildPracticeInterviewWidgetTitle } from '../utils/interviewerDisplay';
import { onboardingApi } from '../../../api/onboardingApi';
import ClickSparkWrapper from '../../../components/common/ClickSparkWrapper';
import AudioReactiveParticles from '../../../components/common/AudioReactiveParticles';
import { useGeminiTtsStream } from '../hooks/useGeminiTtsStream';
import { useOpenAiRealtimeStt } from '../hooks/useOpenAiRealtimeStt';
import { useChatScrollFade } from '../hooks/useChatScrollFade';
import { vibeService } from '../../../services/VibeService';
import { stripEmojis } from '../../../utils/stripEmojis';
import { maskVendorNamesForClient } from '../../../utils/vendorLogMask';
import './AIInterviewInterface.css';
import ReactMarkdown from 'react-markdown';
import FeedbackDashboard from './FeedbackDashboard';
import { ThinkingLabel } from './ThinkingLabel';
import { ThinkingOrb } from 'thinking-orbs';
import {
  DEFAULT_MOCK_INTERVIEW_TOKEN,
  getPracticeFocusTipAfterDash,
  MOCK_INTERVIEW_PRACTICE_NOTICE,
  MOCK_PRACTICE_FOCUS_AREAS,
} from '../constants';

interface InterviewState {
  active: boolean;
  listening: boolean;
  processing: boolean;
  questionCount: number;
  maxQuestions: number;
  currentQuestion: string;
  candidateName: string;
  interviewType: string;
  micMuted: boolean;
}

interface Message {
  id: string;
  sender: 'ai' | 'user';
  text: string;
  timestamp: string;
}

interface WebSocketMessage {
  type: string;
  text?: string;
  question?: string;
  audio_url?: string;
  audio_mode?: 'stream' | 'url' | string;
  stream_id?: string;
  data?: string;
  silence_duration?: number;
  silence_threshold?: number;
  transcribed_text?: string;
  question_number?: number;
  message?: string;
  interview_complete?: boolean;
  hide_user_message?: boolean;
  feedback?: any;
  spoken_feedback?: {
    spoken_text: string;
    audio_url: string;
    audio_file: string;
  };
  total_questions?: number;
  max_questions?: number;
  responses?: any[];
  // GD-specific properties
  session_id?: string;
  topic?: string;
  topic_description?: string;
  round?: string;
  closing?: boolean;
  sequence?: string[];
  statements?: any[];
  round_state?: any;
  rounds?: any;
  participants?: any;
  conversation_history?: any[];
  active?: boolean;
  // GD AI response properties
  speaker_id?: string;
  speaker_name?: string;
  audio_file?: string;
  audio_data?: string;
}

interface RoundSessionData {
  sessionId: string;
  aiSessionId?: string;
  firstQuestion: string;
  audioUrl?: string;
  audioMode?: string;
  maxQuestions?: number;
}

interface AIInterviewInterfaceProps {
  // Auto-start mode for onboarding - skips the widget and starts mock interview directly
  isOnboarding?: boolean;
  // Pre-created round session from custom interview flow
  roundSession?: RoundSessionData;
}

/** Shown in place of token + Begin row while mock AI interview is connecting */
const CONNECTING_LOADER_MESSAGES = [
  'Reading preferences',
  'Reading Resume',
  'Personalising Interview',
  'searching for your AI interviewer',
] as const;

const AIInterviewInterface: React.FC<AIInterviewInterfaceProps> = ({ isOnboarding = false, roundSession }) => {
  const navigate = useNavigate();
  const [interviewState, setInterviewState] = useState<InterviewState>({
    active: false,
    listening: false,
    processing: false,
    questionCount: 0,
    maxQuestions: INTERVIEW_CONFIG.MAX_QUESTIONS,
    currentQuestion: '',
    candidateName: '',
    interviewType: 'general',
    micMuted: false
  });
  const interviewActiveRef = useRef(interviewState.active);
  interviewActiveRef.current = interviewState.active;

  const [aiCurrentlySpeaking, setAiCurrentlySpeaking] = useState(false);
  const aiCurrentlySpeakingRef = useRef(aiCurrentlySpeaking);
  aiCurrentlySpeakingRef.current = aiCurrentlySpeaking;

  const [messages, setMessages] = useState<Message[]>([]);
  const [status, setStatus] = useState('Initializing interview...');
  const [liveTranscription, setLiveTranscription] = useState('');
  const [showLiveTranscription, setShowLiveTranscription] = useState(false);
  const [silenceProgress, setSilenceProgress] = useState(0);
  const [showSilenceIndicator, setShowSilenceIndicator] = useState(false);
  const [silenceText, setSilenceText] = useState('0s');
  const [readyToListen, setReadyToListen] = useState(false);
  const [audioLevels, setAudioLevels] = useState<number[]>(new Array(50).fill(0));
  const [currentVoiceLevel, setCurrentVoiceLevel] = useState(0);
  const [frequencyData, setFrequencyData] = useState<Uint8Array>(new Uint8Array(128));
  const [decibelLevel, setDecibelLevel] = useState(-100);
  const [waveformActive, setWaveformActive] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  /** After interview_complete: show feedback orb + unblurred sphere while closing TTS plays */
  const [postInterviewWrapUp, setPostInterviewWrapUp] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const showFeedbackRef = useRef(false);
  showFeedbackRef.current = showFeedback;
  const pendingFeedbackRef = useRef<{ feedback: any; total_questions?: number } | null>(null);
  /** True until closing TTS (stream or URL) finishes — blocks feedback dashboard */
  const awaitClosingAudioRef = useRef(false);
  const geminiStreamPlaybackActiveRef = useRef(false);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [tokenError, setTokenError] = useState<string>('');
  const [selectedInterviewType, setSelectedInterviewType] = useState<'coding' | 'mock_ai' | 'group_discussion' | null>(null);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showStartOverlay, setShowStartOverlay] = useState(true);
  const [practiceWidgetTitle, setPracticeWidgetTitle] = useState('Professional Practice Interview with Clara');
  /** Practice mock: full notice + spinner in a modal while startInterview runs */
  const [mockStartLoadingModalOpen, setMockStartLoadingModalOpen] = useState(false);
  /** Random focus tip (after " — ") shown in practice loading modal for this start attempt */
  const [practiceModalFocusTip, setPracticeModalFocusTip] = useState('');
  
  // Audio-reactive particles state
  const [luciAudioURL, setLuciAudioURL] = useState<string | null>(null);
  const [particleTriggerFormation, setParticleTriggerFormation] = useState(false);
  const [particleKey, setParticleKey] = useState(0); // Key to force remount particles with new audio
  const [geminiStreamPlaybackActive, setGeminiStreamPlaybackActive] = useState(false);
  geminiStreamPlaybackActiveRef.current = geminiStreamPlaybackActive;
  
  // Onboarding loading state
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingMessage, setOnboardingMessage] = useState('Preparing your personalized interview...');
  const [onboardingMessages, setOnboardingMessages] = useState<string[]>([]);

  // Readiness check state
  type CheckStatus = 'idle' | 'checking' | 'pass' | 'warning' | 'fail';
  const [noiseCheckStatus, setNoiseCheckStatus] = useState<CheckStatus>('idle');
  const [noiseCheckMessage, setNoiseCheckMessage] = useState('Click to test');
  const [noiseCheckLevels, setNoiseCheckLevels] = useState<number[]>(new Array(24).fill(0));
  const [noiseCheckDb, setNoiseCheckDb] = useState(-100);
  
  const [transcriptionCheckStatus, setTranscriptionCheckStatus] = useState<CheckStatus>('idle');
  const [transcriptionCheckMessage, setTranscriptionCheckMessage] = useState('Click to test');
  const [transcriptionText, setTranscriptionText] = useState('');
  const [transcriptionMatch, setTranscriptionMatch] = useState(0);
  const [isTranscriptionListening, setIsTranscriptionListening] = useState(false);
  
  const [speedCheckStatus, setSpeedCheckStatus] = useState<CheckStatus>('idle');
  const [speedCheckMessage, setSpeedCheckMessage] = useState('Click to test');
  const [speedLatency, setSpeedLatency] = useState(0);
  
  const [showWarningModal, setShowWarningModal] = useState(false);
  
  // Pause timer state
  const [totalPauseTime, setTotalPauseTime] = useState(0); // Total pause time in ms
  const [pauseStartTime, setPauseStartTime] = useState<number | null>(null); // When current pause started
  const [currentPauseElapsed, setCurrentPauseElapsed] = useState(0); // Current pause session elapsed time (for UI updates)
  const pauseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_PAUSE_TIME = 300000; // 5 minutes in ms

  /** Cycles through CONNECTING_LOADER_MESSAGES while status includes "Connecting" */
  const [connectingLoaderIndex, setConnectingLoaderIndex] = useState(0);
  
  const noiseCheckStreamRef = useRef<MediaStream | null>(null);
  const noiseCheckContextRef = useRef<AudioContext | null>(null);
  const noiseCheckAnalyserRef = useRef<AnalyserNode | null>(null);
  const noiseCheckAnimationRef = useRef<number | null>(null);
  const transcriptionRecognitionRef = useRef<any>(null);
  const transcriptionMatchRef = useRef<number>(0);

  const TEST_SENTENCE = "The quick brown fox jumps over the lazy dog";

  const websocketRef = useRef<WebSocket | null>(null);
  const openAiListeningRef = useRef(false);
  const submitOpenAiTurnRef = useRef<(() => Promise<void>) | null>(null);
  const openAiSttStopCaptureRef = useRef<(() => void) | null>(null);
  const openAiSttStartCaptureRef = useRef<((stream: MediaStream) => Promise<void>) | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wsSessionIdRef = useRef<string | undefined>(undefined);
  const intentionalCloseRef = useRef<boolean>(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const userStreamRef = useRef<MediaStream | null>(null);
  const lastUserMessageRef = useRef('');
  const lastAIMessageRef = useRef('');
  const currentUserMessageRef = useRef<string>('');
  const pausedTranscriptRef = useRef<string>('');
  const messageIdCounterRef = useRef<number>(0);
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesScrollRef = useRef<HTMLDivElement>(null);
  const chatMessagesPanelRef = useRef<HTMLDivElement>(null);

  // Dedicated question tracker for bubble IDs - independent of interviewState timing
  const currentQuestionForUserRef = useRef<number>(1);

  // Web Audio API for real-time silence detection
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const silenceDetectionRef = useRef<NodeJS.Timeout | null>(null);
  const lastSpeechTimeRef = useRef<number>(0);
  const speechDetectedRef = useRef<boolean>(false);

  // Audio quality hint system - 5 second rolling average buffers
  // TO DISABLE: Simply comment out the audioQualityHint JSX element
  const noiseFloorBufferRef = useRef<number[]>([]);
  const voiceRMSBufferRef = useRef<number[]>([]);
  const voiceToTotalBufferRef = useRef<number[]>([]);
  const AUDIO_QUALITY_BUFFER_SIZE = 50; // 5 seconds at 100ms intervals
  const [audioQualityHint, setAudioQualityHint] = useState<string | null>(null);
  const speechWithoutTranscriptCountRef = useRef<number>(0); // Count consecutive checks with speech but no transcription
  const lastTranscriptionLengthRef = useRef<number>(0); // Track transcription length to detect updates

  // Echo delay timeout ref - prevents self-hearing when AI audio ends
  const echoDelayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /** True after interview completion / feedback opened — blocks round-session restart and re-arming ASR */
  const interviewCompletedRef = useRef(false);

  // Function refs to avoid stale closures in intervals/timeouts
  const startAutoListeningRef = useRef<(() => Promise<void>) | null>(null);

  // ASR Control Manager - Centralized control for all ASR activation
  const asrControlRef = useRef({
    isBlocked: false,
    blockReason: '',

    blockASR: (reason: string) => {
      asrControlRef.current.isBlocked = true;
      asrControlRef.current.blockReason = reason;
      console.log(`🚫 ASR BLOCKED: ${reason}`);
    },

    unblockASR: () => {
      const wasBlocked = asrControlRef.current.isBlocked;
      const reason = asrControlRef.current.blockReason;
      asrControlRef.current.isBlocked = false;
      asrControlRef.current.blockReason = '';
      if (wasBlocked) {
        console.log(`✅ ASR UNBLOCKED: Was blocked for "${reason}"`);
      }
    },

    canStartListening: () => {
      const canStart = !asrControlRef.current.isBlocked && !aiCurrentlySpeakingRef.current;
      if (!canStart) {
        console.log(`🚫 ASR activation denied - Blocked: ${asrControlRef.current.isBlocked} (${asrControlRef.current.blockReason}), AI Speaking: ${aiCurrentlySpeakingRef.current}`);
      }
      return canStart;
    }
  });

  // Initialize audio context
  const initializeAudioContext = useCallback(() => {
    try {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log('🎵 Audio context initialized');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize audio context:', error);
      return false;
    }
  }, []);

  // Add message to chat
  const addMessageToChat = useCallback((sender: 'ai' | 'user', text: string) => {
    const uniqueId = `msg-${Date.now()}-${messageIdCounterRef.current++}`;
    const message: Message = {
      id: uniqueId,
      sender,
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, message]);
    console.log(`Added ${sender} message:`, text);
  }, []);

  // Update user message with live transcription - STRICT filtering to prevent status messages
  const updateUserMessage = useCallback((text: string) => {
    const currentQuestionForUser = currentQuestionForUserRef.current;
    console.log(`🔄 updateUserMessage called with: "${text}" for Q${currentQuestionForUser} (dedicated tracker)`);
    console.log(`🔍 Current messages count: ${messages.length}, Tracker value: ${currentQuestionForUser}`);

    // STRICT filtering: Block all status messages from reaching speech bubbles
    const statusMessages = [
      'Listening...',
      'Listening for speech...',
      'Speaking...',
      'Recording active - speak now...',
      'Recording active - speak now...',
      'Speak now...',
      'Keep going!',
      'Keep speaking!'
    ];

    // Check if this is a status message
    const isStatusMessage = statusMessages.some(status =>
      text.includes(status) || status.includes(text.trim())
    );

    if (isStatusMessage) {
      console.log(`🚫 BLOCKED status message from speech bubble: "${text}"`);
      return;
    }

    // Only filter out completely empty strings or very short non-word content
    if (!text || text.trim() === '' || text.trim().length < 2) {
      console.log(`🚫 Skipping empty/short text for speech bubble: "${text}"`);
      return;
    }

    // Additional check: Must contain actual letters (not just symbols/emojis)
    const hasActualWords = /[a-zA-Z]/.test(text);
    if (!hasActualWords) {
      console.log(`🚫 Skipping non-word content from speech bubble: "${text}"`);
      return;
    }

    // Merge with any paused transcript so new speech APPENDS rather than overwrites
    let mergedText = text;
    const base = pausedTranscriptRef.current;
    if (base && !text.toLowerCase().startsWith(base.toLowerCase())) {
      mergedText = `${base} ${text}`.trim();
    }

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    setMessages(prev => {
      // Find existing user transcription message for CURRENT question using dedicated tracker
      const currentQuestionId = `user-transcription-q${currentQuestionForUser}`;
      const existingIndex = prev.findIndex(msg => msg.id === currentQuestionId);

      console.log(`🔍 Looking for bubble ID: ${currentQuestionId}, found at index: ${existingIndex}`);
      console.log(`🔍 Existing message IDs: ${prev.map(m => m.id).join(', ')}`);

      if (existingIndex >= 0) {
        // Update existing transcription message with new text
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          text: mergedText
        };
        console.log(`✅ Updated existing bubble for Q${currentQuestionForUser}: "${mergedText}"`);
        return updated;
      } else {
        // Create new transcription message with question-specific ID
        const newMessage: Message = {
          id: currentQuestionId,
          sender: 'user',
          text: mergedText,
          timestamp
        };
        console.log(`✅ Created new bubble for Q${currentQuestionForUser}: "${mergedText}" with ID: ${currentQuestionId}`);
        return [...prev, newMessage];
      }
    });

    currentUserMessageRef.current = mergedText;
  }, [messages.length]);

  // Request browser microphone access
  const requestMicrophoneAccess = useCallback(async () => {
    try {
      console.log('🎤 Requesting browser microphone access...');

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } as any // Cast to any to allow browser-specific constraints
      });

      userStreamRef.current = stream;
      console.log('✅ Browser microphone access granted');
      return stream;

    } catch (error: any) {
      console.error('❌ Microphone access denied:', error);

      let errorMessage = 'Microphone access denied';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Please allow microphone access to continue the interview';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Microphone is being used by another application';
      }

      setStatus(`❌ ${errorMessage}`);
      throw error;
    }
  }, []);

  // Setup Web Audio API with professional audio processing chain
  const setupAudioAnalysis = useCallback(async (stream: MediaStream) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      // Resume audio context if suspended
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      // Create audio source
      const source = audioContextRef.current.createMediaStreamSource(stream);

      // Create professional audio processing chain
      console.log('🎛️ Setting up professional audio processing chain...');

      // 1. Dynamic Range Compressor - normalizes voice levels and reduces background noise
      const compressor = audioContextRef.current.createDynamicsCompressor();
      compressor.threshold.value = -40;    // More aggressive compression for noise reduction
      compressor.knee.value = 40;          // Smooth compression curve
      compressor.ratio.value = 12;         // Strong 12:1 compression ratio
      compressor.attack.value = 0;         // Instant compression onset
      compressor.release.value = 0.25;     // Quick release for natural speech

      // 2. Bandpass Filter - focuses on human voice frequencies
      const filter = audioContextRef.current.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 1000;       // Better voice focus, reduced background noise
      filter.Q.value = 2.0;                // Moderate Q for broader voice range (was 8.30)
      filter.gain.value = 6.0;             // Boost voice frequencies (was 3.0)

      // 3. High-pass filter to remove very low frequencies (air conditioning, etc.)
      const highPassFilter = audioContextRef.current.createBiquadFilter();
      highPassFilter.type = 'highpass';
      highPassFilter.frequency.value = 300; // Remove more low-frequency noise
      highPassFilter.Q.value = 1.0;

      // 4. Analyzer for silence detection
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      // Connect the audio processing chain:
      // Microphone → High-pass → Bandpass → Compressor → Analyzer
      source.connect(highPassFilter);
      highPassFilter.connect(filter);
      filter.connect(compressor);
      compressor.connect(analyser);

      // Store references
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

      console.log('✅ Professional audio processing chain setup complete:');
      console.log('   📡 Microphone → High-pass (200Hz) → Bandpass (800Hz) → Compressor → Analyzer');
      console.log('   🎛️ Compressor: -50dB threshold, 12:1 ratio, 0ms attack, 250ms release');
      console.log('   🔊 Bandpass: 800Hz center, Q=2.0, +6dB gain');

      return true;
    } catch (error) {
      console.error('❌ Failed to setup audio processing chain:', error);
      return false;
    }
  }, []);

  // Stop listening and SUBMIT the current turn (OpenAI Realtime STT)
  const stopBrowserRecording = useCallback(() => {
    if (openAiListeningRef.current) {
      void submitOpenAiTurnRef.current?.();
    }
  }, []);

  // Pause listening: stop capture WITHOUT submitting to backend
  const pauseListening = useCallback(() => {
    if (!openAiListeningRef.current) {
      return;
    }
    console.log('⏸️ Pausing TopSpeech capture (no submit)...');
    openAiListeningRef.current = false;
    openAiSttStopCaptureRef.current?.();
    if (silenceDetectionRef.current) {
      clearTimeout(silenceDetectionRef.current);
      silenceDetectionRef.current = null;
    }
    setInterviewState(prev => ({ ...prev, listening: false }));
    setShowSilenceIndicator(false);
    setStatus('⏸️ Interview paused');
  }, []);

  // Enhanced silence detection with noise filtering and voice activity detection
  const startRealTimeSilenceDetection = useCallback(() => {
    if (!analyserRef.current || !dataArrayRef.current) {
      console.warn('⚠️ Audio analysis not setup, cannot detect silence');
      return;
    }

    const SILENCE_THRESHOLD = 2000; // 2 seconds
    const SPEECH_ENERGY_THRESHOLD = 0.08; // Higher threshold for better noise rejection
    const NOISE_FLOOR_THRESHOLD = 1.04; // Higher minimum noise floor for background noise
    const VOICE_FREQUENCY_MIN = 400; // Hz - Tighter voice range, less background noise
    const VOICE_FREQUENCY_MAX = 1800; // Hz - Narrower range for better voice isolation

    let silenceStartTime: number | null = null;
    let speechDetected = false;
    let noiseFloor = 0.04; // Higher adaptive noise floor for better background rejection
    let speechConfidenceBuffer: number[] = []; // Buffer for speech confidence
    const CONFIDENCE_BUFFER_SIZE = 12; // More samples for smoother, more stable detection
    const recordingStartTime = Date.now(); // Track when recording started for waiting timer

    const checkAudioLevel = () => {
      if (!analyserRef.current || !dataArrayRef.current) return;

      // Get both frequency and time domain data
      // @ts-ignore - TypeScript library definition issue with Uint8Array<ArrayBufferLike>
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      const frequencyData = Array.from(dataArrayRef.current);

      // Calculate sample rate and frequency resolution
      const sampleRate = audioContextRef.current?.sampleRate || 44100;
      const nyquist = sampleRate / 2;
      const frequencyResolution = nyquist / frequencyData.length;

      // Find frequency bins corresponding to human voice range
      const voiceStartBin = Math.floor(VOICE_FREQUENCY_MIN / frequencyResolution);
      const voiceEndBin = Math.floor(VOICE_FREQUENCY_MAX / frequencyResolution);

      // Calculate energy in voice frequency range
      let voiceEnergy = 0;
      let totalEnergy = 0;

      for (let i = 0; i < frequencyData.length; i++) {
        const normalizedValue = frequencyData[i] / 255;
        totalEnergy += normalizedValue * normalizedValue;

        // Only count energy in voice frequency range
        if (i >= voiceStartBin && i <= voiceEndBin) {
          voiceEnergy += normalizedValue * normalizedValue;
        }
      }

      // Calculate RMS values
      const totalRMS = Math.sqrt(totalEnergy / frequencyData.length);
      const voiceRMS = Math.sqrt(voiceEnergy / (voiceEndBin - voiceStartBin));

      // Adaptive noise floor calculation
      if (totalRMS < noiseFloor * 1.5) {
        noiseFloor = noiseFloor * 0.95 + totalRMS * 0.05; // Slowly adapt to noise floor
      }

      // Voice activity detection with multiple criteria
      const energyAboveNoise = totalRMS > (noiseFloor + NOISE_FLOOR_THRESHOLD);
      const voiceEnergySignificant = voiceRMS > SPEECH_ENERGY_THRESHOLD;
      const voiceToTotalRatio = totalRMS > 0 ? (voiceRMS / totalRMS) : 0;
      const voiceRatioGood = voiceToTotalRatio > 0.3; // Voice should be significant portion of total energy

      // Calculate speech confidence score (0-1)
      let speechConfidence = 0;
      if (energyAboveNoise) speechConfidence += 0.3;
      if (voiceEnergySignificant) speechConfidence += 0.4;
      if (voiceRatioGood) speechConfidence += 0.3;

      // Add to confidence buffer for smoothing
      speechConfidenceBuffer.push(speechConfidence);
      if (speechConfidenceBuffer.length > CONFIDENCE_BUFFER_SIZE) {
        speechConfidenceBuffer.shift();
      }

      // Calculate average confidence
      const avgConfidence = speechConfidenceBuffer.reduce((a, b) => a + b, 0) / speechConfidenceBuffer.length;

      const currentTime = Date.now();

      // Calculate decibel levels for waveform visualization
      const voiceRMSdB = voiceRMS > 0 ? 20 * Math.log10(voiceRMS) : -100; // Convert to dB
      const totalRMSdB = totalRMS > 0 ? 20 * Math.log10(totalRMS) : -100; // Convert to dB

      // Enhanced silence detection: less sensitive to background noise
      const SILENCE_DB_THRESHOLD = -7.0; // Less sensitive threshold for better noise rejection
      const isAboveSilenceThreshold = voiceRMSdB > SILENCE_DB_THRESHOLD;

      // Combine confidence-based detection with decibel threshold - stricter requirements
      const hasSpeech = (avgConfidence > 0.7) && isAboveSilenceThreshold;

      // ========== AUDIO QUALITY HINT SYSTEM (additive, does not affect existing logic) ==========
      // Collect samples into rolling buffers for 5-second average
      noiseFloorBufferRef.current.push(noiseFloor);
      voiceRMSBufferRef.current.push(voiceRMS);
      voiceToTotalBufferRef.current.push(voiceToTotalRatio);

      // Keep buffer size limited to 5 seconds worth of samples
      if (noiseFloorBufferRef.current.length > AUDIO_QUALITY_BUFFER_SIZE) {
        noiseFloorBufferRef.current.shift();
        voiceRMSBufferRef.current.shift();
        voiceToTotalBufferRef.current.shift();
      }

      // Track speech without transcription (voice detected but Web Speech API not picking it up)
      if (hasSpeech) {
        speechWithoutTranscriptCountRef.current++;
      } else {
        speechWithoutTranscriptCountRef.current = 0;
      }

      // Only calculate hint when buffer is full (5 seconds of data)
      if (noiseFloorBufferRef.current.length === AUDIO_QUALITY_BUFFER_SIZE) {
        const avgNoiseFloor = noiseFloorBufferRef.current.reduce((a, b) => a + b, 0) / AUDIO_QUALITY_BUFFER_SIZE;
        const avgVoiceRMS = voiceRMSBufferRef.current.reduce((a, b) => a + b, 0) / AUDIO_QUALITY_BUFFER_SIZE;
        const avgVoiceToTotal = voiceToTotalBufferRef.current.reduce((a, b) => a + b, 0) / AUDIO_QUALITY_BUFFER_SIZE;

        // Benchmarks (tunable)
        const NOISE_FLOOR_BENCHMARK = 0.12;
        const VOICE_RMS_BENCHMARK = 0.08;
        const VOICE_ACTIVITY_THRESHOLD = 0.15; // Detect voice-like activity even if quiet
        const SPEECH_WITHOUT_TRANSCRIPT_THRESHOLD = 30; // ~3 seconds of speech without transcription

        // Check if there's voice-like activity (energy in voice frequency band) but too quiet
        const hasVoiceActivity = avgVoiceToTotal > VOICE_ACTIVITY_THRESHOLD;
        const isVoiceTooQuiet = avgVoiceRMS < VOICE_RMS_BENCHMARK;
        
        // Check if speech is detected but no transcription is coming through
        const hasSpeechButNoTranscript = speechWithoutTranscriptCountRef.current >= SPEECH_WITHOUT_TRANSCRIPT_THRESHOLD;

        if (avgNoiseFloor > NOISE_FLOOR_BENCHMARK && avgVoiceToTotal < 0.25) {
          setAudioQualityHint('Background noise is high');
        } else if (hasSpeechButNoTranscript) {
          // Voice detected by VAD but Web Speech API isn't transcribing
          setAudioQualityHint('Talk a lil louder');
        } else if (hasVoiceActivity && isVoiceTooQuiet) {
          // Voice-like activity detected but volume is too low
          setAudioQualityHint('Speak louder');
        } else {
          setAudioQualityHint(null);
        }
      }
      // ========== END AUDIO QUALITY HINT SYSTEM ==========

      // Map dB range (-100dB to 0dB) to display range (0 to 1)
      const minDB = -60; // Minimum dB to display (anything below is silence)
      const maxDB = 0;   // Maximum dB (0dB = full scale)

      // Calculate normalized levels for waveform (0-1 range)
      const voiceLevelNormalized = Math.max(0, Math.min(1, (voiceRMSdB - minDB) / (maxDB - minDB)));
      const totalLevelNormalized = Math.max(0, Math.min(1, (totalRMSdB - minDB) / (maxDB - minDB)));

      // Use voice level for display, with minimum visibility
      const displayLevel = Math.max(voiceLevelNormalized, 0.02);
      setCurrentVoiceLevel(displayLevel);

      // Enhanced waveform visualization with frequency-based bars
      const barCount = 50; // Increased from 20 to 50 for more detailed visualization
      const frequencyBinSize = Math.max(1, Math.floor(frequencyData.length / barCount));
      const newAudioLevels: number[] = [];

      for (let i = 0; i < barCount; i++) {
        // Calculate frequency range for this bar
        const startBin = i * frequencyBinSize;
        const endBin = Math.min(startBin + frequencyBinSize, frequencyData.length);

        // Get average frequency for this bar's range
        let barLevel = 0;
        for (let j = startBin; j < endBin; j++) {
          barLevel += frequencyData[j] || 0;
        }
        barLevel = barLevel / (endBin - startBin);

        // Convert to normalized level (0-1)
        const normalizedFreq = Math.min(barLevel / 255, 1.0);

        // Apply frequency-based sensitivity
        let sensitivity = 1.0;
        if (i < barCount * 0.3) {
          sensitivity = 1.8; // Bass frequencies - higher sensitivity
        } else if (i > barCount * 0.7) {
          sensitivity = 1.4; // Treble frequencies - moderate sensitivity
        } else {
          sensitivity = 2.2; // Mid frequencies (voice range) - highest sensitivity
        }

        // Apply decibel-based scaling
        const barLevelDB = normalizedFreq > 0 ? 20 * Math.log10(normalizedFreq) : -100;
        const minBarDB = -50;
        const maxBarDB = 0;
        const dbNormalized = Math.max(0, Math.min(1, (barLevelDB - minBarDB) / (maxBarDB - minBarDB)));

        // Combine frequency and decibel scaling with sensitivity
        const finalLevel = Math.min(1.0, dbNormalized * sensitivity);

        // Add minimum visibility and smooth transitions
        const displayBarLevel = Math.max(finalLevel, 0.02);
        newAudioLevels.push(displayBarLevel);
      }

      // Update audio levels with smooth animation
      setAudioLevels(newAudioLevels);
      setCurrentVoiceLevel(displayLevel);
      setFrequencyData(Uint8Array.from(frequencyData));
      setDecibelLevel(voiceRMSdB);
      setWaveformActive(hasSpeech);

      // Debug logging (can be removed in production)
      if (currentTime % 1000 < 100) { // Log every ~1 second
        console.log(`🎤 Audio Analysis - Voice dB: ${voiceRMSdB.toFixed(1)}, Total dB: ${totalRMSdB.toFixed(1)}, Above -3dB: ${isAboveSilenceThreshold}, Confidence: ${avgConfidence.toFixed(2)}, Speech: ${hasSpeech}, Active Bars: ${newAudioLevels.filter(l => l > 0.1).length}`);
      }

      if (hasSpeech) {
        // Speech detected with high confidence
        if (!speechDetected) {
          speechDetected = true;
          console.log(`🗣️ Speech detected - Confidence: ${avgConfidence.toFixed(2)}, Voice RMS: ${voiceRMS.toFixed(4)}`);
          setStatus('Speaking detected... keep going!');
          // Don't overwrite existing transcription with status messages
          if (!liveTranscription || liveTranscription === '🎤 Recording active - speak now...') {
            // Don't set status messages in live transcription - leave it empty or keep existing real transcription
            console.log('🚫 Skipping status message in live transcription: "Listening for speech..."');
          }
        } else if (silenceStartTime !== null) {
          // Speech resumed during silence countdown - reset the countdown
          console.log(`🗣️ Speech resumed during countdown - resetting silence timer`);
          setStatus('Speaking resumed... keep going!');
          // Don't overwrite existing transcription during speech resume
        }

        // Reset silence timer whenever speech is detected
        silenceStartTime = null;
        setSilenceProgress(0);
        setSilenceText('2.0s');

      } else {
        // No speech OR silence after speech — countdown starts/continues immediately
        // Use recordingStartTime as initial silence start if not yet set
        if (silenceStartTime === null) {
          // If speech was never detected, start counting from recording start
          // If speech was detected and stopped, this branch won't run (silenceStartTime set above)
          silenceStartTime = speechDetected ? currentTime : recordingStartTime;
          if (!speechDetected) {
            console.log(`⏱️ Timer started immediately - no speech yet`);
          } else {
            console.log(`🤫 Silence started after speech - Confidence dropped to: ${avgConfidence.toFixed(2)}`);
          }
        }

        const silenceDuration = currentTime - silenceStartTime;
        const progress = Math.min(silenceDuration / SILENCE_THRESHOLD, 1.0);
        const remainingTime = Math.max(0, (SILENCE_THRESHOLD - silenceDuration) / 1000);

        setSilenceProgress(progress);
        setSilenceText(`${remainingTime.toFixed(1)}s`);
        setStatus(speechDetected 
          ? `Silence: ${(silenceDuration / 1000).toFixed(1)}s / 2.0s`
          : `Respond within: ${remainingTime.toFixed(1)}s`
        );

        // Check if silence threshold reached
        if (silenceDuration >= SILENCE_THRESHOLD) {
          console.log('✅ Silence threshold reached - stopping recording');
          stopBrowserRecording();
          return; // Stop the detection loop
        }
      }

      // Continue checking
      silenceDetectionRef.current = setTimeout(checkAudioLevel, 100); // Check every 100ms
    };

    // Start the detection loop
    checkAudioLevel();
  }, [stopBrowserRecording]);

  const abortListeningOnSttError = useCallback((message: string) => {
    openAiListeningRef.current = false;
    openAiSttStopCaptureRef.current?.();
    if (silenceDetectionRef.current) {
      clearTimeout(silenceDetectionRef.current);
      silenceDetectionRef.current = null;
    }
    setInterviewState(prev => ({ ...prev, listening: false }));
    setShowLiveTranscription(false);
    setShowSilenceIndicator(false);
    setShowProcessing(false);
    setStatus(
      `Transcription unavailable: ${maskVendorNamesForClient(message)}. Please check your connection and try again.`,
    );
  }, []);

  const abortListeningOnSttErrorRef = useRef(abortListeningOnSttError);
  abortListeningOnSttErrorRef.current = abortListeningOnSttError;

  // Start microphone capture with OpenAI Realtime STT and silence detection
  const startBrowserRecording = useCallback(async () => {
    if (!asrControlRef.current.canStartListening()) {
      console.log('⚠️ Refusing to start recording - ASR blocked or AI speaking');
      return;
    }

    try {
      // Get microphone access if not already available
      let stream = userStreamRef.current;
      if (!stream) {
        stream = await requestMicrophoneAccess();
      }

      console.log('🎤 Starting browser audio recording with silence detection...');

      // Setup Web Audio API for real-time analysis
      const audioSetupSuccess = await setupAudioAnalysis(stream);
      if (!audioSetupSuccess) {
        throw new Error('Failed to setup audio analysis');
      }

      openAiListeningRef.current = true;
      speechDetectedRef.current = false;
      lastSpeechTimeRef.current = 0;
      try {
        await openAiSttStartCaptureRef.current?.(stream);
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : String(error);
        abortListeningOnSttErrorRef.current(msg);
        return;
      }
      startRealTimeSilenceDetection();
      setStatus('Recording - Speak now!');
      setInterviewState(prev => ({ ...prev, listening: true }));
      setShowLiveTranscription(true);
      setShowSilenceIndicator(true);
      setSilenceText('2.0s');
      setSilenceProgress(0);
      console.log('✅ TopSpeech listening started');

    } catch (error: any) {
      console.error('❌ Failed to start listening:', error);
      abortListeningOnSttErrorRef.current(error?.message || 'Failed to start microphone');
    }
  }, [requestMicrophoneAccess, setupAudioAnalysis, startRealTimeSilenceDetection]);

  // Start auto listening (OpenAI Realtime STT)
  const startAutoListening = useCallback(async () => {
    console.log('🔄 startAutoListening called with state:', {
      active: interviewState.active,
      listening: interviewState.listening,
      micMuted: interviewState.micMuted,
      questionCount: interviewState.questionCount
    });

    // Check if interview is active and not already listening
    if (!interviewState.active) {
      console.log('⚠️ Skipping auto-listening - interview not active');
      console.log('🔍 Current interviewState:', interviewState);
      return;
    }

    if (interviewState.listening) {
      console.log('⚠️ Skipping auto-listening - already listening');
      return;
    }

    if (!asrControlRef.current.canStartListening()) {
      console.log('⚠️ Skipping auto-listening - ASR blocked or AI speaking');
      return;
    }

    // Check if WebSocket is connected
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
      console.log('⚠️ WebSocket not connected, cannot start listening');
      setStatus('❌ Connection lost - cannot start listening');
      return;
    }

    try {
      console.log('🎤 Starting browser-based listening...');
      setStatus('Preparing to listen...');

      // Start browser recording instead of backend API call
      await startBrowserRecording();

    } catch (error: any) {
      console.error('❌ Failed to start auto-listening:', error);
      setStatus(`❌ Error starting listening: ${error.message}`);
    }
  }, [interviewState.active, interviewState.listening, interviewState.questionCount, startBrowserRecording]);

  // Keep function refs updated to avoid stale closures in intervals/timeouts
  startAutoListeningRef.current = startAutoListening;

  const openFeedbackDashboard = useCallback((payload?: any) => {
    interviewCompletedRef.current = true;
    if (payload?.feedback) {
      setFeedbackData(payload.feedback);
    } else {
      setFeedbackData(null);
    }
    setShowFeedback(true);
    setShowStartOverlay(false);
    setPostInterviewWrapUp(false);
    setShowProcessing(false);
  }, []);

  const flushPendingFeedback = useCallback(() => {
    const pending = pendingFeedbackRef.current;
    if (!pending?.feedback) {
      return;
    }
    pendingFeedbackRef.current = null;
    openFeedbackDashboard(pending);
  }, [openFeedbackDashboard]);

  const clearClosingAudioGate = useCallback(() => {
    awaitClosingAudioRef.current = false;
    flushPendingFeedback();
  }, [flushPendingFeedback]);

  const queueFeedbackDashboard = useCallback((payload: { feedback: any; total_questions?: number }) => {
    pendingFeedbackRef.current = payload;
    if (
      !awaitClosingAudioRef.current &&
      !geminiStreamPlaybackActiveRef.current &&
      !aiCurrentlySpeakingRef.current
    ) {
      flushPendingFeedback();
    }
  }, [flushPendingFeedback]);

  // Handle when Luci's audio ends (from particles component)
  const handleLuciAudioEnd = useCallback(() => {
    console.log('✅ Luci audio finished (from particles)');

    asrControlRef.current.unblockASR();
    setParticleTriggerFormation(false);
    setLuciAudioURL(null);

    if (echoDelayTimeoutRef.current) {
      clearTimeout(echoDelayTimeoutRef.current);
    }

    if (interviewCompletedRef.current || showFeedbackRef.current || !interviewActiveRef.current) {
      setAiCurrentlySpeaking(false);
      if (interviewCompletedRef.current) {
        clearClosingAudioGate();
      }
      return;
    }

    setStatus('✅ AI finished speaking - preparing to listen...');

    echoDelayTimeoutRef.current = setTimeout(() => {
      if (interviewCompletedRef.current || showFeedbackRef.current || !interviewActiveRef.current) {
        setAiCurrentlySpeaking(false);
        return;
      }
      setAiCurrentlySpeaking(false);
      setReadyToListen(true);
      setStatus('🎤 Ready - speak now');
      console.log('🎤 Echo delay complete (400ms) - listening enabled');
    }, 400);

    console.log('⏳ AI audio completed - starting 400ms echo delay before listening');
  }, [clearClosingAudioGate]);

  const {
    handleStreamStart,
    handleStreamChunk,
    handleStreamDone,
    handleStreamError,
    cleanup: cleanupGeminiTtsStream,
    getStreamFrequencyData,
  } = useGeminiTtsStream({
    onStreamStart: () => {
      asrControlRef.current.blockASR('TopVoice stream');
      setAiCurrentlySpeaking(true);
      setGeminiStreamPlaybackActive(true);
      setParticleTriggerFormation(true);
      setStatus('AI is speaking...');
    },
    onStreamDone: () => {
      setGeminiStreamPlaybackActive(false);
      handleLuciAudioEnd();
      clearClosingAudioGate();
    },
    onStreamError: (message) => {
      console.error('TopVoice stream error:', maskVendorNamesForClient(message));
      setGeminiStreamPlaybackActive(false);
      asrControlRef.current.unblockASR();
      setAiCurrentlySpeaking(false);
      setParticleTriggerFormation(false);
      if (!interviewCompletedRef.current && interviewActiveRef.current && !showFeedbackRef.current) {
        setReadyToListen(true);
      }
      clearClosingAudioGate();
    },
  });

  // Play AI audio via particles component
  const playAIAudio = useCallback(async (audioUrl: string) => {
    const shouldArmListening = () =>
      interviewActiveRef.current && !interviewCompletedRef.current && !showFeedbackRef.current;

    if (!audioUrl) {
      console.warn('⚠️ No audio URL provided');
      if (shouldArmListening()) {
        setReadyToListen(true);
      }
      return;
    }

    console.log('🔊 Playing AI audio via particles:', audioUrl);

    // STRICT ASR BLOCKING: Block all ASR activation during audio playback
    asrControlRef.current.blockASR('AI audio playback');
    setAiCurrentlySpeaking(true);
    setGeminiStreamPlaybackActive(false);
    console.log('🎤 AI is now speaking - STRICT ASR blocking activated');

    try {
      // Use authenticated audio fetching with headers instead of direct URL
      console.log('🎵 Fetching authenticated audio with headers...');
      const authenticatedAudioBlobUrl = await fetchAuthenticatedAudio(audioUrl);

      console.log('🎵 Passing audio to particles component...');
      
      // Increment key to force remount of particles component with new audio
      setParticleKey(prev => prev + 1);
      
      // Set the audio URL for the particles component to play
      setLuciAudioURL(authenticatedAudioBlobUrl);
      
      // Trigger particle formation (sphere) and audio playback
      setParticleTriggerFormation(true);
      
      console.log('🎵 Audio handed off to particles component');
    } catch (audioFetchError) {
      console.error('❌ Failed to fetch authenticated audio:', audioFetchError);
      setStatus('❌ Audio fetch failed - starting listening...');

      asrControlRef.current.unblockASR();
      setAiCurrentlySpeaking(false);
      if (shouldArmListening()) {
        setReadyToListen(true);
      }
    }
  }, []);

  const armGeminiStreamSpeaking = useCallback(() => {
    setReadyToListen(false);
    asrControlRef.current.blockASR('TopVoice stream');
    setAiCurrentlySpeaking(true);
    setGeminiStreamPlaybackActive(true);
    setParticleTriggerFormation(true);
    setStatus('AI is speaking...');

    if (openAiListeningRef.current) {
      openAiListeningRef.current = false;
      openAiSttStopCaptureRef.current?.();
      if (silenceDetectionRef.current) {
        clearTimeout(silenceDetectionRef.current);
        silenceDetectionRef.current = null;
      }
      setInterviewState(prev => ({ ...prev, listening: false }));
      setShowLiveTranscription(false);
      setShowSilenceIndicator(false);
    }
  }, []);

  const playMessageAudio = useCallback((data: WebSocketMessage) => {
    if (data.audio_mode === 'stream') {
      if (data.stream_id) {
        void handleStreamStart(data.stream_id);
      } else {
        armGeminiStreamSpeaking();
      }
      return;
    }
    if (data.audio_url) {
      void playAIAudio(data.audio_url);
    } else if (!interviewCompletedRef.current) {
      setReadyToListen(true);
    }
  }, [playAIAudio, armGeminiStreamSpeaking, handleStreamStart]);

  // Handle ASR messages for live transcription
  const handleASRMessage = useCallback((data: any) => {
    console.log('📨 ASR message:', data);

    switch (data.type) {
      case 'partial':
        // Handle partial transcription from ASR service
        const partialText = data.result?.partial || '';
        if (partialText && partialText.trim()) {
          updateUserMessage(partialText);
          setLiveTranscription(partialText);
          console.log(`🔄 Live ASR transcription: ${partialText.substring(0, 50)}...`);
        }
        break;

      case 'final':
        // Handle final transcription from ASR service
        const finalText = data.result?.text || '';
        if (finalText && finalText.trim()) {
          updateUserMessage(finalText);
          setLiveTranscription(finalText);
          console.log(`✅ Final ASR transcription: ${finalText.substring(0, 50)}...`);
        }
        break;

      case 'transcription_partial':
        // Legacy format support - Update live transcription in user message bubble
        if (data.text && data.text.trim()) {
          updateUserMessage(data.text);
          setLiveTranscription(data.text);
          console.log(`🔄 Live ASR transcription (legacy): ${data.text.substring(0, 50)}...`);
        }
        break;

      case 'transcription_final':
        // Legacy format support - Finalize the user message
        if (data.text && data.text.trim()) {
          updateUserMessage(data.text); // Mark as final
          setLiveTranscription(data.text);
          console.log(`✅ Final ASR transcription (legacy): ${data.text.substring(0, 50)}...`);
        }
        break;

      case 'error':
      case 'asr_error': {
        const errMsg = maskVendorNamesForClient(
          String(data.message || data.error || 'Transcription error'),
        );
        console.error('❌ ASR error:', errMsg);
        abortListeningOnSttErrorRef.current(errMsg);
        break;
      }

      case 'ready':
        console.log(
          '✅ ASR service ready:',
          maskVendorNamesForClient(String(data.message || '')),
        );
        break;

      default:
        console.log('🔍 Unknown ASR message type:', data.type, data);
    }
  }, [updateUserMessage]);

  const openAiStt = useOpenAiRealtimeStt({
    onMessage: (data) => {
      handleASRMessage(data);
    },
    onError: (message) => {
      const masked = maskVendorNamesForClient(message);
      console.error('TopSpeech error:', masked);
      abortListeningOnSttErrorRef.current(masked);
    },
  });

  const openAiSttDisconnectRef = useRef(openAiStt.disconnect);
  openAiSttDisconnectRef.current = openAiStt.disconnect;
  openAiSttStopCaptureRef.current = openAiStt.stopMicCapture;
  openAiSttStartCaptureRef.current = openAiStt.startMicCapture;

  submitOpenAiTurnRef.current = async () => {
    if (!openAiListeningRef.current) {
      return;
    }
    openAiListeningRef.current = false;
    if (silenceDetectionRef.current) {
      clearTimeout(silenceDetectionRef.current);
      silenceDetectionRef.current = null;
    }
    setInterviewState(prev => ({ ...prev, listening: false }));
    setShowLiveTranscription(false);
    setShowSilenceIndicator(false);
    setShowProcessing(true);
    const questionNumber = currentQuestionForUserRef.current;
    const maxQ = interviewState.maxQuestions;
    if (questionNumber >= maxQ) {
      setPostInterviewWrapUp(true);
      setStatus('Ending interview');
    } else {
      vibeService.trigger('thinking');
      setStatus('🔄 Processing your response...');
    }
    noiseFloorBufferRef.current = [];
    voiceRMSBufferRef.current = [];
    voiceToTotalBufferRef.current = [];
    speechWithoutTranscriptCountRef.current = 0;
    lastTranscriptionLengthRef.current = 0;
    setAudioQualityHint(null);
    pausedTranscriptRef.current = '';
    console.log(`📡 TopSpeech commit for Q${questionNumber}`);
    await openAiStt.commitTurn(questionNumber);
  };

  // Initialize OpenAI Realtime STT WebSocket (/ws/asr)
  const initASRWebSocket = useCallback(async () => {
    try {
      await openAiStt.connect();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to connect TopSpeech WebSocket:', error);
      abortListeningOnSttErrorRef.current(maskVendorNamesForClient(msg));
    }
  }, [openAiStt]);

  const clearTokenRefreshInterval = useCallback(() => {
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }
  }, []);

  const startTokenRefreshInterval = useCallback(() => {
    clearTokenRefreshInterval();
    const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes
    tokenRefreshIntervalRef.current = setInterval(async () => {
      try {
        const authClientModule = await import('../../../api/authClient');
        await authClientModule.default.post('/auth/refresh').then((res) => {
          const newToken = res.data.access_token;
          const expiresIn = res.data.expires_in;
          if (newToken) {
            localStorage.setItem('authToken', newToken);
            const expiresAt = Date.now() + (expiresIn * 1000);
            localStorage.setItem('tokenExpiresAt', expiresAt.toString());
            console.log('✅ [Interview] Proactive token refresh successful');
          }
        });
      } catch (e) {
        console.warn('⚠️ [Interview] Proactive token refresh failed:', e);
      }
    }, REFRESH_INTERVAL_MS);
  }, [clearTokenRefreshInterval]);

  // Initialize WebSocket
  // sessionId is optional - used when starting from a pre-created round session (custom interview flow)
  const initWebSocket = useCallback(async (sessionId?: string) => {
    // Prevent multiple connections
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      console.log('🔄 WebSocket already connected, skipping initialization');
      return;
    }

    // Store sessionId for reconnection
    if (sessionId !== undefined) {
      wsSessionIdRef.current = sessionId;
    }

    // Close existing connection without triggering reconnect
    if (websocketRef.current) {
      console.log('🔄 Closing existing WebSocket connection');
      intentionalCloseRef.current = true;
      websocketRef.current.close();
      websocketRef.current = null;
    }

    try {
      console.log('🔌 Initializing WebSocket connection with header-based auth...');
      if (wsSessionIdRef.current) {
        console.log('🔌 Using session ID:', wsSessionIdRef.current);
      }

      // === DEV/PROD SETTINGS: WEBSOCKET CONNECTION ===
      // Build WebSocket URL, optionally including session_id for round sessions
      const baseWsUrl = API_CONFIG.WEBSOCKET_URL;
      const wsUrl = wsSessionIdRef.current 
        ? `${baseWsUrl}${baseWsUrl.includes('?') ? '&' : '?'}session_id=${encodeURIComponent(wsSessionIdRef.current)}`
        : baseWsUrl;

      // DEVELOPMENT: Use the constructed URL for local development
      websocketRef.current = await createAuthenticatedWebSocket(wsUrl);

      // PRODUCTION: Uncomment the lines below for production deployment
      // const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // const prodBaseWsUrl = `${wsProtocol}//${window.location.host}/ws/listening`;
      // const prodWsUrl = wsSessionIdRef.current 
      //   ? `${prodBaseWsUrl}?session_id=${encodeURIComponent(wsSessionIdRef.current)}`
      //   : prodBaseWsUrl;
      // websocketRef.current = await createAuthenticatedWebSocket(prodWsUrl);

      websocketRef.current.onopen = () => {
        console.log('✅ WebSocket connected with header-based authentication');
        setStatus('✅ Connected to interview system');
        reconnectAttemptsRef.current = 0;

        startTokenRefreshInterval();

        // Initialize ASR WebSocket after main WebSocket is connected
        initASRWebSocket();
      };

      websocketRef.current.onmessage = (event) => {
        const data: WebSocketMessage = JSON.parse(event.data);
        handleWebSocketMessage(data);
      };

      websocketRef.current.onclose = (event) => {
        console.log('❌ WebSocket disconnected', event.code, event.reason);
        websocketRef.current = null;
        clearTokenRefreshInterval();

        if (intentionalCloseRef.current) {
          intentionalCloseRef.current = false;
          return;
        }

        // Auth failure (code 1008) -- refresh token then reconnect via initWebSocket
        if (event.code === 1008) {
          console.log('🔐 WebSocket auth failure -- attempting token refresh + reconnect...');
          setStatus('🔄 Refreshing authentication...');
          (async () => {
            try {
              const authClientModule = await import('../../../api/authClient');
              const res = await authClientModule.default.post('/auth/refresh');
              const newToken = res.data.access_token;
              const expiresIn = res.data.expires_in;
              if (newToken) {
                localStorage.setItem('authToken', newToken);
                const expiresAt = Date.now() + (expiresIn * 1000);
                localStorage.setItem('tokenExpiresAt', expiresAt.toString());
                console.log('✅ Token refreshed, reconnecting WebSocket...');
                reconnectAttemptsRef.current = 0;
                initWebSocket();
              } else {
                throw new Error('No token in refresh response');
              }
            } catch (e) {
              console.error('❌ Token refresh failed, redirecting to login:', e);
              localStorage.removeItem('authToken');
              navigate('/login', { replace: true });
            }
          })();
          return;
        }

        // Non-auth close -- auto-reconnect with exponential backoff
        const MAX_RECONNECT_ATTEMPTS = 3;
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const attempt = reconnectAttemptsRef.current + 1;
          const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
          reconnectAttemptsRef.current = attempt;
          console.log(`🔄 Auto-reconnect attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms...`);
          setStatus(`🔄 Reconnecting (attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS})...`);
          setTimeout(() => initWebSocket(), delay);
        } else {
          console.error(`❌ Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached`);
          setStatus('❌ Connection lost - please refresh the page');
        }
      };

      websocketRef.current.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
      };

    } catch (error) {
      console.error('❌ Failed to connect WebSocket:', error);
      setStatus('❌ Failed to connect to interview system');
    }
  }, [initASRWebSocket, navigate, startTokenRefreshInterval, clearTokenRefreshInterval]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data: WebSocketMessage) => {
    console.log('📨 WebSocket message:', data);

    switch (data.type) {
      // === STANDARD INTERVIEW EVENTS ===
      case 'listening_started':
        setInterviewState(prev => ({ ...prev, listening: true }));
        setStatus('Listening... speak now!');
        setShowLiveTranscription(true);
        setShowSilenceIndicator(true);
        setSilenceText('2.0s'); // Start countdown immediately
        setSilenceProgress(0);
        break;

      case 'transcription_update':
        // Only update transcription if we have actual text, don't overwrite with "Listening..."
        if (data.text && data.text.trim()) {
          setLiveTranscription(data.text);
          updateUserMessage(data.text); // Update the chat bubble too
          setShowLiveTranscription(true);
          console.log(`🔄 Backend transcription update: "${data.text}"`);
        }

        if (data.silence_duration === 0) {
          setSilenceProgress(0);
          setStatus('Speaking detected... keep going!');
          setShowLiveTranscription(true);
        } else if (data.silence_duration && data.silence_threshold) {
          const progress = Math.min(data.silence_duration / data.silence_threshold, 1.0);
          setSilenceProgress(progress);
          const remainingTime = Math.max(0, data.silence_threshold - data.silence_duration);
          setSilenceText(`${remainingTime.toFixed(1)}s`);
          setStatus(`Silence: ${(data.silence_duration / 1000).toFixed(1)}s / ${(data.silence_threshold / 1000).toFixed(1)}s`);
        } else {
          setStatus('Listening... keep speaking!');
          setShowLiveTranscription(true);
        }
        break;

      case 'transcription_partial':
        // Handle live transcription updates from backend
        if (data.text && data.text.trim()) {
          updateUserMessage(data.text); // Mark as live
          setLiveTranscription(data.text);
          console.log(`🔄 Backend live transcription: ${data.text.substring(0, 50)}...`);
        }
        break;

      case 'transcription_final':
        // Handle final transcription from backend
        if (data.text && data.text.trim()) {
          updateUserMessage(data.text); // Mark as final
          setLiveTranscription(data.text);
          console.log(`✅ Backend final transcription: ${data.text.substring(0, 50)}...`);
        }
        break;

      case 'transcription_complete':
        setInterviewState(prev => ({ ...prev, listening: false }));
        setShowLiveTranscription(false);
        setShowSilenceIndicator(false);
        setShowProcessing(true);
        if (interviewState.questionCount >= interviewState.maxQuestions) {
          setPostInterviewWrapUp(true);
          setStatus('Ending interview');
        } else {
          vibeService.trigger('thinking');
          setStatus('🔄 Processing your response...');
        }
        break;

      case 'next_question':
        setShowProcessing(false);

        // COMPLETE ASR RESET FOR NEW QUESTION
        console.log('🔄 Starting complete ASR reset for new question...');

        // Update interview state FIRST, including question count for next user response
        const nextQuestionNumber = data.question_number || (interviewState.questionCount + 1);
        setInterviewState(prev => ({
          ...prev,
          processing: false,
          questionCount: nextQuestionNumber,
          currentQuestion: data.question || prev.currentQuestion,
          maxQuestions:
            typeof data.max_questions === 'number' && data.max_questions > 0
              ? data.max_questions
              : prev.maxQuestions,
        }));

        // CRITICAL: Update dedicated question tracker for user responses IMMEDIATELY
        currentQuestionForUserRef.current = nextQuestionNumber;
        console.log(`🔄 Updated questionCount to ${nextQuestionNumber} AND dedicated tracker to ${nextQuestionNumber} for next user response`);

        // Clear live transcription for next question
        setLiveTranscription('');
        currentUserMessageRef.current = '';
        pausedTranscriptRef.current = '';
        console.log(`🧹 Cleared live transcription for Q${nextQuestionNumber}`);

        // DON'T process transcribed_text here - live transcription already handled it
        // The live transcription bubble created during recording is the final one
        console.log(`🚫 Skipping backend transcribed_text processing - live transcription already created the user bubble`);

        if (data.transcribed_text) {
          lastUserMessageRef.current = data.transcribed_text;
        }

        // Add AI question
        if (data.question && data.question !== lastAIMessageRef.current) {
          addMessageToChat('ai', data.question);
          lastAIMessageRef.current = data.question;
        }

        if (data.interview_complete) {
          setStatus('Interview completed! Thank you for your time.');
          interviewCompletedRef.current = true;
          setInterviewState(prev => ({ ...prev, active: false }));
        } else {
          setStatus('AI is speaking...');
        }

        // Play AI audio if available
        playMessageAudio(data);
        break;

      case 'user_response':
        // Handle user response messages from backend
        console.log('📝 Received user_response message from backend:', data);

        if (data.text && data.text.trim()) {
          // Update user message bubble with backend-processed response
          updateUserMessage(data.text);
          console.log(`✅ Updated user message from backend: ${data.text.substring(0, 50)}...`);
        }

        if (data.message) {
          setStatus(data.message);
        }
        break;

      case 'no_speech_response':
        // Clear processing state so mic button becomes enabled after audio plays
        setShowProcessing(false);
        setInterviewState(prev => ({ ...prev, listening: false }));
        setShowLiveTranscription(false);
        setShowSilenceIndicator(false);

        // Only add AI's natural response if it's not a duplicate
        if (data.question && data.question !== lastAIMessageRef.current) {
          addMessageToChat('ai', data.question);
          lastAIMessageRef.current = data.question;
        }

        setStatus('🤖 AI is speaking... Please try again after');

        // Play AI audio if available - after audio ends, mic will be enabled for toggling
        playMessageAudio(data);
        break;

      case 'interview_complete_with_feedback':
        console.log('🎉 Interview completed with comprehensive feedback');
        interviewCompletedRef.current = true;
        setShowProcessing(false);
        setInterviewState(prev => ({ ...prev, active: false, listening: false, processing: false }));
        vibeService.trigger('celebration');
        setShowLiveTranscription(false);
        setShowSilenceIndicator(false);

        // Store feedback data and show feedback dashboard
        if (data.feedback) {
          console.log('📊 Showing feedback dashboard with data:', data.feedback);
          openFeedbackDashboard(data);
        }

        // Add completion message to chat
        if (data.message) {
          addMessageToChat('ai', data.message);
        }

        // Display feedback summary in chat
        if (data.feedback) {
          const feedbackSummary = `🎯 Interview Complete! Your score: ${data.feedback.overall_score}/100 (${data.feedback.readiness_level})`;
          addMessageToChat('ai', feedbackSummary);
        }

        // Auto-play comprehensive feedback audio if available
        if (data.spoken_feedback && data.spoken_feedback.audio_url) {
          console.log('🔊 Auto-playing comprehensive feedback audio:', data.spoken_feedback.audio_url);
          setStatus('Playing your interview feedback...');
          playAIAudio(data.spoken_feedback.audio_url);
        } else {
          setStatus('Interview completed! Review your feedback below.');
        }

        // Log feedback data for debugging
        if (data.feedback) {
          console.log('📊 Comprehensive feedback received:', {
            overall_score: data.feedback.overall_score,
            readiness_level: data.feedback.readiness_level,
            total_questions: data.total_questions,
            has_spoken_feedback: !!data.spoken_feedback
          });
        }
        break;

      case 'interview_complete':
        console.log('🎉 Interview completed (basic)', {
          hasMessage: !!data.message,
          messageLength: data.message?.length ?? 0,
          hasAudioUrl: !!data.audio_url,
          audioUrl: data.audio_url ?? null,
          hasFeedback: !!data.feedback,
        });
        interviewCompletedRef.current = true;
        setShowProcessing(true);
        setPostInterviewWrapUp(true);
        setShowStartOverlay(false);
        setMockStartLoadingModalOpen(false);
        setInterviewState(prev => ({ ...prev, active: false, listening: false, processing: false }));
        vibeService.trigger('celebration');
        setShowLiveTranscription(false);
        setShowSilenceIndicator(false);

        // Add completion message to chat
        if (data.message) {
          addMessageToChat('ai', data.message);
        }

        // Play closing audio (stream or bucket fallback)
        const hasClosingAudio = data.audio_mode === 'stream' || !!data.audio_url;
        awaitClosingAudioRef.current = hasClosingAudio;
        setStatus('Ending interview');
        playMessageAudio(data);
        if (data.feedback) {
          console.log('📊 Feedback payload received in basic completion, queueing dashboard after closing audio.');
          queueFeedbackDashboard({ feedback: data.feedback, total_questions: data.total_questions });
        } else if (!hasClosingAudio) {
          awaitClosingAudioRef.current = false;
        } else {
          window.setTimeout(() => {
            if (awaitClosingAudioRef.current && interviewCompletedRef.current) {
              clearClosingAudioGate();
            }
          }, 120_000);
        }
        break;

      case 'interview_feedback_ready':
        console.log('📊 Interview feedback ready (background)', data.feedback);
        if (data.feedback) {
          queueFeedbackDashboard({ feedback: data.feedback, total_questions: data.total_questions });
          if (!awaitClosingAudioRef.current && !showFeedbackRef.current) {
            setStatus('Ending interview');
          }
        }
        break;

      case 'tts_stream_start':
        if (data.stream_id) {
          void handleStreamStart(data.stream_id);
        }
        break;

      case 'tts_chunk':
        if (data.stream_id && data.data) {
          void handleStreamChunk(data.stream_id, data.data);
        }
        break;

      case 'tts_stream_done':
        if (data.stream_id) {
          handleStreamDone(data.stream_id);
        }
        break;

      case 'tts_stream_error':
        if (data.stream_id) {
          handleStreamError(data.stream_id, data.message);
        }
        break;

      case 'first_question':
      case 'tts_fallback':
        if (data.audio_url) {
          if (interviewCompletedRef.current) {
            awaitClosingAudioRef.current = true;
          }
          void playAIAudio(data.audio_url);
        }
        break;

      case 'error':
        console.error('❌ Interview error:', data.message);
        setStatus(`❌ Error: ${data.message}`);
        setInterviewState(prev => ({ ...prev, listening: false }));
        setShowLiveTranscription(false);
        setShowSilenceIndicator(false);
        break;
    }
  }, [addMessageToChat, playAIAudio, playMessageAudio, handleStreamStart, handleStreamChunk, handleStreamDone, handleStreamError, startAutoListening, openFeedbackDashboard, queueFeedbackDashboard, clearClosingAudioGate, interviewState.questionCount, interviewState.maxQuestions]);

  // ===== READINESS CHECK FUNCTIONS =====
  
  // Helper to detect Edge on macOS
  const isEdgeMacOSCheck = useCallback((): boolean => {
    const ua = navigator.userAgent;
    return ua.includes('Edg/') && ua.includes('Mac OS');
  }, []);

  // Calculate word match percentage
  const calculateMatch = useCallback((expected: string, actual: string): number => {
    if (!actual.trim()) return 0;
    const expectedWords = expected.toLowerCase().split(/\s+/);
    const actualWords = actual.toLowerCase().split(/\s+/);
    const matches = expectedWords.filter(word => 
      actualWords.some(w => w.includes(word) || word.includes(w))
    ).length;
    return Math.round((matches / expectedWords.length) * 100);
  }, []);

  // Start noise check
  const startNoiseCheck = useCallback(async () => {
    setNoiseCheckStatus('checking');
    setNoiseCheckMessage('Accessing microphone...');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } 
      });
      noiseCheckStreamRef.current = stream;

      const audioContext = new AudioContext();
      noiseCheckContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      noiseCheckAnalyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      let checkDuration = 0;
      const totalCheckTime = 5000; // 5 seconds for noise analysis

      const analyzeAudio = () => {
        if (!noiseCheckAnalyserRef.current) return;

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
        const dbLevel = average > 0 ? 20 * Math.log10(average / 255) : -100;

        const levels: number[] = [];
        const step = Math.floor(bufferLength / 24);
        for (let i = 0; i < 24; i++) {
          levels.push(dataArray[i * step] / 255);
        }

        setNoiseCheckLevels(levels);
        setNoiseCheckDb(dbLevel);
        setNoiseCheckMessage('Analyzing...');

        checkDuration += 50;

        if (checkDuration >= totalCheckTime) {
          let finalStatus: CheckStatus = 'pass';
          let finalMessage = 'Environment is quiet';

          if (dbLevel > -25) {
            finalStatus = 'fail';
            finalMessage = 'Too much noise';
          } else if (dbLevel > -40) {
            finalStatus = 'warning';
            finalMessage = 'Moderate noise';
          }

          setNoiseCheckStatus(finalStatus);
          setNoiseCheckMessage(finalMessage);
          stream.getTracks().forEach(track => track.stop());
          noiseCheckStreamRef.current = null;
          return;
        }

        noiseCheckAnimationRef.current = requestAnimationFrame(analyzeAudio);
      };

      analyzeAudio();
    } catch (error) {
      console.error('Noise check error:', error);
      setNoiseCheckStatus('fail');
      setNoiseCheckMessage('Mic access denied');
    }
  }, []);

  // Start transcription test
  const startTranscriptionTest = useCallback(() => {
    if (isEdgeMacOSCheck()) {
      setTranscriptionCheckStatus('warning');
      setTranscriptionCheckMessage('Server transcription (Edge/macOS)');
      setTranscriptionMatch(100);
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setTranscriptionCheckStatus('fail');
      setTranscriptionCheckMessage('Not supported');
      return;
    }

    setTranscriptionCheckStatus('checking');
    setTranscriptionText('');
    setTranscriptionMatch(0);
    setIsTranscriptionListening(true);
    setTranscriptionCheckMessage('Listening...');

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      transcriptionRecognitionRef.current = recognition;

      recognition.onresult = (event: any) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        const match = calculateMatch(TEST_SENTENCE, transcript);
        setTranscriptionText(transcript);
        setTranscriptionMatch(match);
        transcriptionMatchRef.current = match;
      };

      recognition.onend = () => {
        setIsTranscriptionListening(false);
        const match = transcriptionMatchRef.current;
        if (match >= 80) {
          setTranscriptionCheckStatus('pass');
          setTranscriptionCheckMessage('Working well');
        } else if (match >= 50) {
          setTranscriptionCheckStatus('warning');
          setTranscriptionCheckMessage('Partially working');
        } else {
          setTranscriptionCheckStatus('fail');
          setTranscriptionCheckMessage('Low accuracy');
        }
      };

      recognition.onerror = (event: any) => {
        setIsTranscriptionListening(false);
        if (event.error === 'network') {
          setTranscriptionCheckStatus('warning');
          setTranscriptionCheckMessage('Using server transcription');
          setTranscriptionMatch(100);
        } else {
          setTranscriptionCheckStatus('fail');
          setTranscriptionCheckMessage('Test failed');
        }
      };

      recognition.start();
      setTimeout(() => { try { recognition.stop(); } catch (e) {} }, 8000);
    } catch (error) {
      setTranscriptionCheckStatus('fail');
      setIsTranscriptionListening(false);
      setTranscriptionCheckMessage('Failed to start');
    }
  }, [isEdgeMacOSCheck, calculateMatch]);

  // Start speed test
  const startSpeedTest = useCallback(async () => {
    setSpeedCheckStatus('checking');
    setSpeedCheckMessage('Testing...');

    try {
      const latencies: number[] = [];
      for (let i = 0; i < 3; i++) {
        const startTime = performance.now();
        await fetch(window.location.origin, { method: 'HEAD', cache: 'no-store' }).catch(() => {});
        latencies.push(performance.now() - startTime);
      }

      const avgLatency = Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length);
      setSpeedLatency(avgLatency);

      let finalStatus: CheckStatus = 'pass';
      let finalMessage = `Good (${avgLatency}ms)`;

      if (avgLatency > 300) {
        finalStatus = 'fail';
        finalMessage = `Slow (${avgLatency}ms)`;
      } else if (avgLatency > 100) {
        finalStatus = 'warning';
        finalMessage = `Moderate (${avgLatency}ms)`;
      }

      setSpeedCheckStatus(finalStatus);
      setSpeedCheckMessage(finalMessage);
    } catch (error) {
      setSpeedCheckStatus('fail');
      setSpeedCheckMessage('Test failed');
    }
  }, []);

  // Check if all tests are done
  const allTestsRun = noiseCheckStatus !== 'idle' && noiseCheckStatus !== 'checking' &&
                      transcriptionCheckStatus !== 'idle' && transcriptionCheckStatus !== 'checking' &&
                      speedCheckStatus !== 'idle' && speedCheckStatus !== 'checking';

  const hasWarningsOrFails = noiseCheckStatus === 'warning' || noiseCheckStatus === 'fail' ||
                             transcriptionCheckStatus === 'warning' || transcriptionCheckStatus === 'fail' ||
                             speedCheckStatus === 'warning' || speedCheckStatus === 'fail';

  // Get status dot class
  const getStatusDotClass = (checkStatus: CheckStatus): string => {
    switch (checkStatus) {
      case 'pass': return 'check-dot green';
      case 'warning': return 'check-dot yellow';
      case 'fail': return 'check-dot red';
      case 'checking': return 'check-dot checking';
      default: return 'check-dot idle';
    }
  };

  // ===== END READINESS CHECK FUNCTIONS =====

  // Auto-start interview
  const autoStartInterview = useCallback(async () => {
    const isCoding = selectedInterviewType === 'coding';
    const isGroupDiscussion = selectedInterviewType === 'group_discussion';
    const isMockAi = selectedInterviewType === 'mock_ai';

    if (!selectedInterviewType) {
      setTokenError('Please select an interview type');
      setStatus('❌ Interview type required');
      return;
    }

    const formattedToken = DEFAULT_MOCK_INTERVIEW_TOKEN;

    setStatus(
      isCoding
        ? '🧩 Opening coding interview...'
        : isGroupDiscussion
          ? '👥 Opening group discussion...'
          : isMockAi
            ? '🔄 Starting interview...'
            : '🔄 Connecting to AI interviewer...'
    );

    try {
      if (isCoding) {
        navigate('/coding_challenge');
        return;
      }

      if (isGroupDiscussion) {
        navigate('/group-discussion', {
          state: { token: formattedToken },
        });
        return;
      }

      const focusPick =
        MOCK_PRACTICE_FOCUS_AREAS[Math.floor(Math.random() * MOCK_PRACTICE_FOCUS_AREAS.length)] ?? '';
      setPracticeModalFocusTip(getPracticeFocusTipAfterDash(focusPick));
      setMockStartLoadingModalOpen(true);
      vibeService.trigger('loading');

      let difficulty = 'medium';
      let experienceFocus = 'varied';
      let mode = 'professional';
      let responseFeedback = true;
      try {
        const settingsResponse = await settingsApi.getInterviewSettings();
        if (settingsResponse.success && settingsResponse.data?.settings) {
          const s = settingsResponse.data.settings;
          difficulty = s.difficulty;
          experienceFocus = s.experience_focus;
          mode = s.mode;
          responseFeedback = s.response_feedback ?? true;
        }
      } catch {
        // use defaults; backend also merges from DB
      }

      const interviewTypeForApi = 'general';
      const data = await interviewApi.startInterview({
        interview_type: interviewTypeForApi,
        token: formattedToken,
        difficulty,
        experience_focus: experienceFocus,
        mode,
        response_feedback: responseFeedback,
      });

      if (data.success) {
        interviewCompletedRef.current = false;
        setPostInterviewWrapUp(false);

        const newState = {
          active: true,
          questionCount: 1,
          currentQuestion: data.question,
          candidateName: interviewState.candidateName,
          interviewType: interviewTypeForApi,
          listening: false,
          processing: false,
          maxQuestions: data.max_questions ?? INTERVIEW_CONFIG.MAX_QUESTIONS,
          micMuted: false,
        };

        setInterviewState(prev => ({ ...prev, ...newState }));
        setShowStartOverlay(false);
        setMockStartLoadingModalOpen(false);
        addMessageToChat('ai', data.question);
        currentQuestionForUserRef.current = 1;
        initWebSocket();

        if (data.audio_mode === 'stream') {
          armGeminiStreamSpeaking();
          setStatus('✅ Interview started - AI is speaking...');
        } else if (data.audio_url) {
          playAIAudio(data.audio_url);
          setStatus('✅ Interview started - AI is speaking...');
        } else {
          setReadyToListen(true);
          setStatus('✅ Interview started');
        }

        vibeService.dismiss();
      } else {
        throw new Error(data.message || 'Failed to start interview');
      }
    } catch (error: any) {
      console.error('❌ Failed to auto-start interview:', error);
      vibeService.dismiss();

      let errorMessage = 'Failed to start interview automatically';

      if (error.response?.status === 400) {
        const errorDetail = error.response.data?.detail;
        if (typeof errorDetail === 'object' && errorDetail.error === 'Token validation failed') {
          setTokenError('Wrong or expired token');
          setStatus('❌ Token validation failed');
          return;
        }
        if (typeof errorDetail === 'string' && errorDetail.includes('token')) {
          setTokenError('Wrong or expired token');
          setStatus('❌ Token validation failed');
          return;
        }
      }

      setTokenError('');

      if (error.message?.includes('Failed to fetch')) {
        errorMessage = 'Cannot connect to backend. Please ensure the backend server is running on port 8000.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setStatus(`❌ ${errorMessage}`);

      setTimeout(() => {
        if (window.confirm('Failed to start interview. Would you like to retry?')) {
          autoStartInterview();
        }
      }, 2000);
    } finally {
      if (isMockAi) {
        setMockStartLoadingModalOpen(false);
      }
    }
  }, [addMessageToChat, initWebSocket, playAIAudio, armGeminiStreamSpeaking, interviewState.candidateName, navigate, selectedInterviewType]);

  // Toggle microphone: allow mute option that pauses submission & analyses current words
  const toggleMicrophone = useCallback(() => {
    console.log('🎤 Microphone button clicked');

    if (!interviewState.active) {
      console.log('⚠️ Interview not active, ignoring mic toggle');
      return;
    }

    // Disable mic toggle while AI is speaking
    if (aiCurrentlySpeaking) {
      console.log('⚠️ Cannot toggle microphone while AI is speaking');
      return;
    }

    // Disable mic toggle while processing response
    if (showProcessing) {
      console.log('⚠️ Cannot toggle microphone while processing response');
      return;
    }

    // If we are currently listening/recording, treat this as:
    // "pause here, keep what I have so far"
    if (interviewState.listening) {
      // Check if pause time is exhausted before allowing pause
      if (totalPauseTime >= MAX_PAUSE_TIME) {
        console.log('⚠️ Pause time exhausted - cannot pause');
        setStatus('⚠️ Pause time exhausted - cannot pause');
        return;
      }
      
      console.log('⏸️ Mic clicked while listening – pausing recording (no submit)');
      // Remember what we have so far so new speech can be appended
      pausedTranscriptRef.current = currentUserMessageRef.current || liveTranscription;
      pauseListening();
      // Reflect muted mic and start tracking pause time
      setInterviewState(prev => ({ ...prev, micMuted: true }));
      setPauseStartTime(Date.now());
      setStatus('⏸️ Interview paused');
      return;
    }

    // Otherwise, we are currently NOT listening; treat as toggle pause/resume
    const wasMuted = interviewState.micMuted;

    if (!wasMuted) {
      // Check if pause time is exhausted before allowing pause
      if (totalPauseTime >= MAX_PAUSE_TIME) {
        console.log('⚠️ Pause time exhausted - cannot pause');
        setStatus('⚠️ Pause time exhausted - cannot pause');
        return;
      }
      
      // Going from unmuted → muted (while not actively listening)
      setInterviewState(prev => ({ ...prev, micMuted: true }));
      setPauseStartTime(Date.now());
      setStatus('⏸️ Interview paused');
    } else {
      // Going from muted → unmuted; accumulate pause time and resume
      if (pauseStartTime !== null) {
        const pauseElapsed = Date.now() - pauseStartTime;
        setTotalPauseTime(prev => Math.min(prev + pauseElapsed, MAX_PAUSE_TIME));
        setPauseStartTime(null);
        setCurrentPauseElapsed(0);
      }
      
      setInterviewState(prev => ({ ...prev, micMuted: false }));
      setStatus('▶️ Interview resumed');
      if (interviewState.active && !interviewState.listening) {
        startAutoListening();
      }
    }
  }, [interviewState.active, interviewState.listening, interviewState.micMuted, aiCurrentlySpeaking, showProcessing, pauseListening, startAutoListening, liveTranscription, totalPauseTime, pauseStartTime, MAX_PAUSE_TIME]);

  // Spacebar keyboard shortcut to toggle microphone
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only trigger if interview is active and spacebar is pressed
      // Ignore if user is typing in an input field
      if (event.code === 'Space' && interviewState.active) {
        const target = event.target as HTMLElement;
        const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
        
        if (!isInputField) {
          event.preventDefault(); // Prevent page scroll
          toggleMicrophone();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [interviewState.active, toggleMicrophone]);

  // Track transcription updates for audio quality hint (voice detected but no transcription)
  useEffect(() => {
    if (liveTranscription.length > lastTranscriptionLengthRef.current) {
      // Transcription grew - reset the "speech without transcript" counter
      speechWithoutTranscriptCountRef.current = 0;
      lastTranscriptionLengthRef.current = liveTranscription.length;
    }
  }, [liveTranscription]);

  // Cycle connecting-stage messages on start overlay (GD/coding) or mock start loading modal
  useEffect(() => {
    const cycle =
      status.includes('Connecting') ||
      (mockStartLoadingModalOpen && selectedInterviewType === 'mock_ai');
    if (!cycle) {
      setConnectingLoaderIndex(0);
      return;
    }
    setConnectingLoaderIndex(0);
    const id = window.setInterval(() => {
      setConnectingLoaderIndex((prev) => (prev + 1) % CONNECTING_LOADER_MESSAGES.length);
    }, 2200);
    return () => window.clearInterval(id);
  }, [status, mockStartLoadingModalOpen, selectedInterviewType]);

  // Pause timer - update every second ONLY when user has actively paused
  // Does NOT count during: AI speaking, processing response, or when not paused
  useEffect(() => {
    // Only count pause time when: paused AND active AND NOT processing AND NOT AI speaking
    const shouldCountPause = interviewState.micMuted && 
                              interviewState.active && 
                              pauseStartTime !== null &&
                              !showProcessing && 
                              !aiCurrentlySpeaking;
    
    if (shouldCountPause) {
      // Start interval to update pause time every second
      pauseTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - pauseStartTime;
        setCurrentPauseElapsed(elapsed); // Update for UI re-render
        const newTotalPause = totalPauseTime + elapsed;
        
        // Check if max pause time reached
        if (newTotalPause >= MAX_PAUSE_TIME) {
          // Auto-resume and disable further pausing
          setTotalPauseTime(MAX_PAUSE_TIME);
          setPauseStartTime(null);
          setCurrentPauseElapsed(0);
          setInterviewState(prev => ({ ...prev, micMuted: false }));
          setStatus('⚠️ Pause time exhausted - resuming interview');
          
          // Clear interval first to prevent re-entry
          if (pauseTimerRef.current) {
            clearInterval(pauseTimerRef.current);
            pauseTimerRef.current = null;
          }
          
          // Start listening with a small delay to ensure state updates have propagated
          // Use refs to get the latest function versions (avoids stale closure)
          setTimeout(() => {
            startAutoListeningRef.current?.();
          }, 100);
        }
      }, 1000);

      return () => {
        if (pauseTimerRef.current) {
          clearInterval(pauseTimerRef.current);
          pauseTimerRef.current = null;
        }
      };
    } else {
      // Clear interval if conditions no longer met
      if (pauseTimerRef.current) {
        clearInterval(pauseTimerRef.current);
        pauseTimerRef.current = null;
      }
    }
  // Note: startAutoListening accessed via refs to avoid stale closures
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewState.micMuted, interviewState.active, pauseStartTime, totalPauseTime, interviewState.listening, showProcessing, aiCurrentlySpeaking, MAX_PAUSE_TIME]);

  // Core cleanup logic for ending an interview / GD session
  const performEndInterview = useCallback(async () => {
    console.log('📞 End interview confirmed');

    if (!interviewState.active) {
      console.log('⚠️ Interview not active, ignoring end call');
      return;
    }

    console.log('🔚 Ending interview...');

    try {
      await interviewApi.endInterview('');
    } catch (err) {
      console.warn('⚠️ Failed to notify backend of interview end:', err);
    }

    setShowStartOverlay(false);

    setInterviewState(prev => ({
      ...prev,
      active: false,
      listening: false,
      processing: false
    }));

    // Stop any currently playing audio
    const audioElements = document.querySelectorAll('audio');
    audioElements.forEach(audio => {
      if (!audio.paused) {
        console.log('🔇 Stopping audio playback');
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
      }
    });

    // Stop silence detection
    if (silenceDetectionRef.current) {
      clearTimeout(silenceDetectionRef.current);
      silenceDetectionRef.current = null;
      console.log('⏹️ Silence detection stopped');
    }

    // Stop OpenAI STT capture and disconnect /ws/asr
    if (openAiListeningRef.current) {
      openAiListeningRef.current = false;
      openAiSttStopCaptureRef.current?.();
    }
    openAiSttDisconnectRef.current();

    // Close WebSocket connection (mark intentional to prevent auto-reconnect)
    clearTokenRefreshInterval();
    intentionalCloseRef.current = true;
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
      console.log('🔌 WebSocket connection closed');
    }

    // Stop user microphone stream
    if (userStreamRef.current) {
      userStreamRef.current.getTracks().forEach(track => track.stop());
      userStreamRef.current = null;
      console.log('🎤 Microphone stream stopped');
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
      console.log('🎵 Audio context closed');
    }

    // Reset all UI states
    setShowLiveTranscription(false);
    setShowSilenceIndicator(false);
    setSilenceProgress(0);
    setSilenceText('0s');
    setAudioLevels(new Array(50).fill(0));
    setCurrentVoiceLevel(0);
    setWaveformActive(false);

    addMessageToChat('ai', 'Interview session ended. Thank you for your time!');
    setStatus('Interview ended');

    // Redirect to candidate dashboard after a short delay to allow UI to update
    setTimeout(() => {
      navigate('/candidate');
    }, 800);
  }, [interviewState.active, addMessageToChat, navigate]);

  // Show confirmation widget before actually ending
  const endInterview = useCallback(() => {
    console.log('📞 End interview button clicked');

    if (!interviewState.active) {
      console.log('⚠️ Interview not active, ignoring end call');
      return;
    }

    setShowEndConfirm(true);
  }, [interviewState.active]);

  // State-watching useEffect to handle listening when interview becomes active
  useEffect(() => {
    if (
      interviewState.active &&
      readyToListen &&
      !aiCurrentlySpeaking &&
      !asrControlRef.current.isBlocked
    ) {
      console.log('🎤 Interview active and ready - starting listening immediately');

      // FRESH ASR START: Ensure ASR is unblocked for new questions
      asrControlRef.current.unblockASR();
      console.log('✅ ASR unblocked for fresh question start');

      startAutoListening();

      setReadyToListen(false); // Reset flag
    } else if (readyToListen && aiCurrentlySpeaking) {
      console.log('🚫 BLOCKED listening activation - AI is currently speaking');
    }
  }, [interviewState.active, readyToListen, aiCurrentlySpeaking, startAutoListening]);

  // Stop OpenAI STT capture when AI starts speaking to prevent self-hearing
  useEffect(() => {
    if (aiCurrentlySpeaking && openAiListeningRef.current) {
      openAiListeningRef.current = false;
      openAiSttStopCaptureRef.current?.();
      if (silenceDetectionRef.current) {
        clearTimeout(silenceDetectionRef.current);
        silenceDetectionRef.current = null;
      }
      setInterviewState(prev => ({ ...prev, listening: false }));
      setShowSilenceIndicator(false);
      console.log('🔇 Stopped TopSpeech capture - AI is speaking');
    }
  }, [aiCurrentlySpeaking]);

  // Auto-scroll chat messages to bottom when new messages arrive (but NOT when feedback is shown)
  useEffect(() => {
    // Only auto-scroll if feedback dashboard is NOT being shown
    if (!showFeedback && chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, showFeedback]);

  useChatScrollFade(chatMessagesScrollRef, chatMessagesPanelRef, messages);

  // Prevent accidental page refresh / tab close while interview is in progress
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    if (interviewState.active && !showFeedback) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [interviewState.active, showFeedback]);

  // Initialize on component mount with 2-second delay
  useEffect(() => {
    console.log('🚀 Modern AI Interview Interface initialized');
    setStatus('Initializing interview system...');

    // Initialize audio context immediately
    initializeAudioContext();

    // Add 2-second delay before showing ready status
    const initTimer = setTimeout(() => {
      console.log('✅ 2-second initialization delay completed');
      setStatus('Ready to start interview...');
    }, 2000);

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up AI Interview Interface...');

      // Clear initialization timer
      clearTimeout(initTimer);

      // Clear echo delay timeout to prevent state updates on unmounted component
      if (echoDelayTimeoutRef.current) {
        clearTimeout(echoDelayTimeoutRef.current);
        echoDelayTimeoutRef.current = null;
      }

      // Clear token refresh interval
      clearTokenRefreshInterval();

      // Close WebSocket connections (mark as intentional to prevent reconnect)
      intentionalCloseRef.current = true;
      if (websocketRef.current) {
        console.log('🔌 Closing WebSocket connection on cleanup');
        websocketRef.current.close();
        websocketRef.current = null;
      }

      openAiSttDisconnectRef.current();

      // Stop user microphone stream
      if (userStreamRef.current) {
        console.log('🎤 Stopping microphone stream on cleanup');
        userStreamRef.current.getTracks().forEach(track => track.stop());
        userStreamRef.current = null;
      }

      // Close audio context
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        console.log('🎵 Closing audio context on cleanup');
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      cleanupGeminiTtsStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Cleanup only on unmount — do not tear down WS/TTS when interview ends

  // Start overlay title from saved interview settings (mode + interviewer persona)
  useEffect(() => {
    if (!showStartOverlay || isOnboarding) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await settingsApi.getInterviewSettings();
        if (cancelled || !response.success || !response.data?.settings) return;
        const { settings, interviewer_preview } = response.data;
        setPracticeWidgetTitle(
          buildPracticeInterviewWidgetTitle(settings, interviewer_preview?.name)
        );
      } catch {
        // keep default title
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showStartOverlay, isOnboarding]);

  // Load interview facts/tips for onboarding loading screen
  useEffect(() => {
    if (!isOnboarding) return;
    
    const loadMessages = async () => {
      try {
        const res = await fetch('/inter_view_facts.txt');
        if (res.ok) {
          const text = await res.text();
          const lines = text
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0);
          if (lines.length > 0) {
            setOnboardingMessages(lines);
            // Set initial random message
            setOnboardingMessage(lines[Math.floor(Math.random() * lines.length)]);
          }
        }
      } catch (error) {
        console.log('Could not load interview facts, using defaults');
      }
    };
    
    loadMessages();
  }, [isOnboarding]);

  // Rotate onboarding messages during loading
  useEffect(() => {
    if (!onboardingLoading || onboardingMessages.length === 0) return;
    
    const messageInterval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * onboardingMessages.length);
      setOnboardingMessage(onboardingMessages[randomIndex]);
    }, 3000); // Change message every 3 seconds
    
    return () => clearInterval(messageInterval);
  }, [onboardingLoading, onboardingMessages]);

  // Auto-start onboarding interview - skips widget and starts mock AI interview directly
  useEffect(() => {
    if (!isOnboarding) return;
    
    // Hide the start overlay for onboarding and show loading
    setShowStartOverlay(false);
    setSelectedInterviewType('mock_ai');
    setOnboardingLoading(true);
    
    // Auto-start the interview after a brief delay to ensure component is ready
    const onboardingTimer = setTimeout(async () => {
      console.log('🚀 Auto-starting onboarding mock interview...');
      vibeService.trigger('loading');
      
      try {
        // Use the dedicated onboarding interview start API
        const data = await onboardingApi.startOnboardingInterview();
        
        console.log('✅ Onboarding interview started:', data);
        
        if (data.success) {
          // Hide loading screen
          setOnboardingLoading(false);
          interviewCompletedRef.current = false;

          const newState = {
            active: true,
            questionCount: 1,
            currentQuestion: data.question,
            candidateName: 'Candidate',
            interviewType: 'general',
            listening: false,
            processing: false,
            maxQuestions: data.max_questions ?? INTERVIEW_CONFIG.MAX_QUESTIONS,
            micMuted: false
          };
          
          setInterviewState(prev => ({ ...prev, ...newState }));
          addMessageToChat('ai', data.question);
          currentQuestionForUserRef.current = 1;
          initWebSocket();
          
          console.log('🔊 Onboarding interview audio_mode:', data.audio_mode, 'audio_url:', data.audio_url);
          
          if (data.audio_mode === 'stream') {
            armGeminiStreamSpeaking();
            setStatus('✅ Interview started - AI is speaking...');
          } else if (data.audio_url) {
            try {
              await playAIAudio(data.audio_url);
            } catch (audioError) {
              console.error('❌ Failed to play onboarding audio:', audioError);
              setReadyToListen(true);
            }
            setStatus('✅ Interview started - AI is speaking...');
          } else {
            console.log('ℹ️ No audio_url in onboarding response, starting listening directly');
            setReadyToListen(true);
            setStatus('✅ Interview started');
          }
          
          vibeService.dismiss();
        } else {
          throw new Error(data.message || 'Failed to start interview');
        }
      } catch (error: any) {
        console.error('❌ Failed to auto-start onboarding interview:', error);
        vibeService.dismiss();
        setOnboardingLoading(false);
        setStatus('❌ Failed to start interview. Please try again.');
        // Show the overlay so user can retry manually
        setShowStartOverlay(true);
      }
    }, 1500); // Reduced wait time since we show loading screen
    
    return () => clearTimeout(onboardingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboarding]);

  // Auto-start from a pre-created round session (custom interview flow)
  useEffect(() => {
    if (!roundSession) return;
    if (showFeedback || interviewCompletedRef.current) return;

    console.log('🎯 Starting interview from pre-created round session:', {
      sessionId: roundSession.sessionId,
      aiSessionId: roundSession.aiSessionId,
      hasFirstQuestion: !!roundSession.firstQuestion,
    });

    setShowStartOverlay(false);
    setSelectedInterviewType('mock_ai');

    const timer = setTimeout(() => {
      if (showFeedbackRef.current || interviewCompletedRef.current) return;

      interviewCompletedRef.current = false;

      const newState = {
        active: true,
        questionCount: 1,
        currentQuestion: roundSession.firstQuestion,
        candidateName: 'Candidate',
        interviewType: 'general',
        listening: false,
        processing: false,
        maxQuestions: roundSession.maxQuestions ?? INTERVIEW_CONFIG.MAX_QUESTIONS,
        micMuted: false,
      };

      setInterviewState(prev => ({ ...prev, ...newState }));
      addMessageToChat('ai', roundSession.firstQuestion);
      currentQuestionForUserRef.current = 1;
      // Pass the session ID to WebSocket so backend can associate the connection with the round session
      initWebSocket(roundSession.sessionId);

      if (roundSession.audioMode === 'stream') {
        armGeminiStreamSpeaking();
        setStatus('Interview started - AI is speaking...');
      } else if (roundSession.audioUrl) {
        playAIAudio(roundSession.audioUrl);
        setStatus('Interview started');
      } else {
        setReadyToListen(true);
        setStatus('Interview started');
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundSession, showFeedback]);

  return (
    <div className="ai-interview-interface">
      {/* Feedback Dashboard Overlay */}
      {showFeedback && (
        <FeedbackDashboard
          feedbackData={feedbackData}
          candidateName={interviewState.candidateName}
          interviewMessages={messages}
          onClose={() => {
            setShowFeedback(false);
            setFeedbackData(null);
            // After reviewing feedback, return to candidate dashboard to start fresh next time
            setShowStartOverlay(false);
            navigate('/candidate');
          }}
          onPlayAudio={playAIAudio}
        />
      )}

      {/* Onboarding Loading Overlay */}
      {onboardingLoading && (
        <div className="onboarding-loading-overlay">
          <div className="onboarding-loading-content">
            <div className="onboarding-loading-logo">
              <img src="/image_i_do1.png" alt="Luci" className="luci-logo-loading" />
            </div>
            <div className="onboarding-loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <h2 className="onboarding-loading-title">Preparing Your Interview</h2>
            <p className="onboarding-loading-message">{onboardingMessage}</p>
            <div className="onboarding-loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}

      {/* End Interview Confirmation Modal */}
      {showEndConfirm && (
        <div className="end-confirm-overlay">
          <div className="end-confirm-widget">
            <h2 className="end-confirm-title">End interview?</h2>
            <p className="end-confirm-text">
              Are you sure you want to end the interview mid way?
            </p>
            <div className="end-confirm-actions">
              <button
                type="button"
                className="end-confirm-btn secondary"
                onClick={() => setShowEndConfirm(false)}
              >
                cancel
              </button>
              <button
                type="button"
                className="end-confirm-btn danger"
                onClick={() => {
                  setShowEndConfirm(false);
                  performEndInterview();
                }}
              >
                yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Practice mock: notice + status lines while startInterview is in flight */}
      {showStartOverlay &&
        !interviewState.active &&
        !showFeedback &&
        mockStartLoadingModalOpen &&
        selectedInterviewType === 'mock_ai' && (
          <div
            className="mock-interview-loading-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-busy="true"
            aria-labelledby="mock-interview-loading-title"
          >
            <div className="mock-interview-loading-modal">
              <h2 id="mock-interview-loading-title" className="mock-interview-loading-modal__title">
                Preparing your session
              </h2>
              <p className="mock-interview-loading-modal__notice">{MOCK_INTERVIEW_PRACTICE_NOTICE}</p>
              {practiceModalFocusTip ? (
                <p className="mock-interview-loading-modal__focus-tip">{practiceModalFocusTip}</p>
              ) : null}
              <div className="interview-connecting-loader mock-interview-loading-modal__loader">
                <span className="interview-connecting-loader__spinner" aria-hidden="true" />
                <p className="interview-connecting-loader__text" key={connectingLoaderIndex}>
                  {CONNECTING_LOADER_MESSAGES[connectingLoaderIndex]}
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Start Interview Overlay */}
      {showStartOverlay && !interviewState.active && !showFeedback && (
        <div className="start-interview-overlay">
          <div className="start-interview-widget">
            <div className="widget-header">
              <button
                className="back-button"
                onClick={() => window.history.back()}
                title="Go back"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="currentColor" />
                </svg>
              </button>
              <div className="widget-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H11V21H5V3H13V9H21Z" fill="currentColor" />
                  <path d="M16 10V12H18V10H20V12H22V14H20V16H18V14H16V12H14V10H16Z" fill="currentColor" />
                </svg>
              </div>
              <h1 className="widget-title">{practiceWidgetTitle}</h1>
              <p className="widget-subtitle">Professional interview simulation powered by advanced AI assessment</p>
            </div>

            <div className="widget-content">
              {/* System Readiness Checks */}
              <div className="readiness-checks-section">
                <div className="checks-header">
                  <label className="input-label">System Checks</label>
                  <button
                    className="run-all-btn"
                    onClick={async () => {
                      await startSpeedTest();
                      setTimeout(() => {
                        startNoiseCheck();
                      }, 500);
                    }}
                    disabled={speedCheckStatus === 'checking' || noiseCheckStatus === 'checking' || isTranscriptionListening}
                  >
                    Run All
                  </button>
                </div>
                
                {/* Row 1: Network + Noise side by side */}
                <div className="checks-row-top">
                  <div className="check-compact">
                    <div className="check-compact-header">
                      <span className={getStatusDotClass(speedCheckStatus)} />
                      <span className="check-compact-title">Network</span>
                      {speedLatency > 0 && <span className="check-compact-metric">{speedLatency}ms</span>}
                    </div>
                    <div className="check-compact-status">{speedCheckMessage || 'Test connection'}</div>
                    <button
                      className="check-compact-btn"
                      onClick={startSpeedTest}
                      disabled={speedCheckStatus === 'checking'}
                    >
                      {speedCheckStatus === 'checking' ? '...' : speedCheckStatus === 'idle' ? 'Test' : 'Retest'}
                    </button>
                  </div>

                  <div className="checks-row-divider" />

                  <div className="check-compact">
                    <div className="check-compact-header">
                      <span className={getStatusDotClass(noiseCheckStatus)} />
                      <span className="check-compact-title">
                        {noiseCheckStatus === 'fail' ? 'Too much noise' : 'Background Noise'}
                      </span>
                      {noiseCheckDb > -100 && <span className="check-compact-metric">{noiseCheckDb.toFixed(0)}dB</span>}
                    </div>
                    <div className="check-compact-waveform">
                      {noiseCheckLevels.length > 0 ? (
                        noiseCheckLevels.map((level, idx) => (
                          <div key={idx} className="wave-bar-sm" style={{ height: `${Math.max(level * 100, 10)}%` }} />
                        ))
                      ) : (
                        <span className="check-compact-status">{noiseCheckMessage || 'Test ambient noise'}</span>
                      )}
                    </div>
                    <button
                      className="check-compact-btn"
                      onClick={startNoiseCheck}
                      disabled={noiseCheckStatus === 'checking'}
                    >
                      {noiseCheckStatus === 'checking' ? '...' : noiseCheckStatus === 'idle' ? 'Test' : 'Retest'}
                    </button>
                  </div>
                </div>

                {/* Row 2: Speech test - full width */}
                <div className="checks-row-speech">
                  <div className="speech-test-header">
                    <span className={getStatusDotClass(transcriptionCheckStatus)} />
                    <span className="speech-test-title">Speech Recognition Test</span>
                    {transcriptionMatch > 0 && <span className="speech-test-accuracy">{transcriptionMatch}%</span>}
                  </div>
                  
                  <div className="speech-test-content">
                    <div className="speech-test-prompt">
                      <span className="speech-label">Read aloud:</span>
                      <span className="speech-sentence">"{TEST_SENTENCE}"</span>
                    </div>
                    
                    {transcriptionText ? (
                      <div className="speech-test-result">
                        <span className="speech-label">You said:</span>
                        <span className="speech-heard">"{transcriptionText}"</span>
                      </div>
                    ) : (
                      <div className="speech-test-hint">
                        {isTranscriptionListening ? 'Listening... speak now' : (transcriptionCheckMessage || 'Click the button and read the sentence above')}
                      </div>
                    )}
                  </div>

                  <button
                    className="speech-test-btn"
                    onClick={startTranscriptionTest}
                    disabled={isTranscriptionListening}
                  >
                    {isTranscriptionListening ? 'Listening...' : transcriptionCheckStatus === 'idle' ? 'Start Speaking' : 'Try Again'}
                  </button>
                </div>
              </div>

              {/* Step 1: Interview Type Selection */}
              <div className="form-section" style={{ marginBottom: '16px' }}>
                <div className="checks-header">
                  <label className="input-label">Choose Interview Type</label>
                  <button
                    type="button"
                    className="interview-settings-link"
                    onClick={() => navigate('/settings')}
                  >
                    Change interview settings
                  </button>
                </div>
                <div className="type-options">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedInterviewType('mock_ai');
                      if (tokenError) setTokenError('');
                    }}
                    className={`type-card ${selectedInterviewType === 'mock_ai' ? 'selected' : ''}`}
                    title="Mock AI interview"
                  >
                    <div className="type-icon" aria-hidden="true">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 8V4H8" />
                        <rect x="4" y="8" width="16" height="12" rx="2" />
                        <circle cx="9" cy="13" r="1" fill="currentColor" />
                        <circle cx="15" cy="13" r="1" fill="currentColor" />
                        <path d="M9 17h6" />
                      </svg>
                    </div>
                    <div className="type-content">
                      <div className="type-title">Mock Interview</div>
                      <div className="type-desc">Q&A with AI</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedInterviewType('coding');
                      if (tokenError) setTokenError('');
                    }}
                    className={`type-card ${selectedInterviewType === 'coding' ? 'selected' : ''}`}
                    title="Coding interview"
                  >
                    <div className="type-icon" aria-hidden="true">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="16 18 22 12 16 6" />
                        <polyline points="8 6 2 12 8 18" />
                        <line x1="14" y1="4" x2="10" y2="20" />
                      </svg>
                    </div>
                    <div className="type-content">
                      <div className="type-title">Coding</div>
                      <div className="type-desc">Live coding tasks</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedInterviewType('group_discussion');
                      if (tokenError) setTokenError('');
                    }}
                    className={`type-card ${selectedInterviewType === 'group_discussion' ? 'selected' : ''}`}
                    title="Group Discussion"
                  >
                    <div className="type-icon" aria-hidden="true">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    </div>
                    <div className="type-content">
                      <div className="type-title">Group Discussion</div>
                      <div className="type-desc">Team discussion</div>
                    </div>
                  </button>
                </div>
              </div>

              {selectedInterviewType && (
                <>
                  {status.includes('Connecting') && selectedInterviewType !== 'mock_ai' ? (
                    <div
                      className="token-and-button-row token-and-button-row--connecting"
                      role="status"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <div className="interview-connecting-loader">
                        <span className="interview-connecting-loader__spinner" aria-hidden="true" />
                        <p
                          className="interview-connecting-loader__text"
                          key={connectingLoaderIndex}
                        >
                          {CONNECTING_LOADER_MESSAGES[connectingLoaderIndex]}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="token-and-button-row token-and-button-row--mock-only">
                      <ClickSparkWrapper sparkColor="#a3e635" sparkCount={10} sparkRadius={20} disabled={status.includes('Failed') || mockStartLoadingModalOpen}>
                        <button
                          type="button"
                          className="start-button row-button"
                          onClick={autoStartInterview}
                          disabled={status.includes('Failed') || mockStartLoadingModalOpen}
                        >
                          <span className="start-button-inner">
                            <span>Begin</span>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path d="M8 5V19L19 12L8 5Z" fill="currentColor" />
                            </svg>
                          </span>
                        </button>
                      </ClickSparkWrapper>
                    </div>
                  )}

                  {tokenError && (
                    <div className="token-error-message">
                      {tokenError}
                    </div>
                  )}
                </>
              )}


              {status.includes('Failed') && (
                <div className="error-card">
                  <div className="error-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor" />
                    </svg>
                  </div>
                  <div className="error-content">
                    <div className="error-title">Connection Failed</div>
                    <div className="error-message">{status}</div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Main Container */}
      <div
        className={`main-container ${!interviewState.active && !postInterviewWrapUp ? 'blurred' : ''}`}
      >
        {/* Video Section */}
        <div className="video-section">
          {/* Header Bar */}
          <div className="header-bar">
            <div className="header-left">
              <div className="record-indicator"></div>
              <img
                src="/image_i_do1.png"
                alt="Luci logo"
                className="session-logo"
              />
              <div className="participants-count">
                2 Participants
              </div>
            </div>
            {showLiveTranscription && (
              <div
                className="header-transcribing"
                role="status"
                aria-live="polite"
              >
                <div className="header-transcribing-orb-wrap">
                  <ThinkingOrb
                    state="composing"
                    size={64}
                    theme="dark"
                    className="header-transcribing-orb"
                    aria-hidden
                  />
                </div>
                <span className="header-transcribing-label">Transcribing</span>
              </div>
            )}
          </div>

          {/* Detached, larger session title */}
          <div className="session-title-large">
          
          </div>

          {/* Audio-Reactive Particles Animation */}
          <div className="particles-container">
            <AudioReactiveParticles
              key={particleKey}
              audioURL={luciAudioURL || undefined}
              onAudioEnd={luciAudioURL ? handleLuciAudioEnd : undefined}
              getExternalFrequencyData={getStreamFrequencyData}
              streamPlaybackActive={geminiStreamPlaybackActive}
              initialPhase="scattered"
              triggerFormation={particleTriggerFormation}
              backgroundMode={false}
              centerOverlay={
                showProcessing || postInterviewWrapUp ? (
                  <ThinkingLabel
                    variant={
                      postInterviewWrapUp ||
                      interviewState.questionCount >= interviewState.maxQuestions
                        ? 'feedback'
                        : 'thinking'
                    }
                  />
                ) : null
              }
            />
          </div>

          {/* Instructional hint - fixed position bottom left */}
          {interviewState.active && (
            <div className="controls-hint-fixed">
              Respond within 2 seconds at your turn, else press Spacebar and pause interview to take more time.
            </div>
          )}

          {/* Pause Timer - shown ONLY when user has actively paused (not during processing/AI speaking) */}
          {interviewState.micMuted && interviewState.active && !showProcessing && !aiCurrentlySpeaking && (
            <div className="pause-timer-container">
              <span className="pause-timer-label">Pause timer:</span>
              <div className="pause-timer-bar">
                <div 
                  className="pause-timer-fill"
                  style={{ 
                    width: `${Math.max(0, 100 - ((totalPauseTime + currentPauseElapsed) / MAX_PAUSE_TIME * 100))}%`,
                  }}
                />
              </div>
              <span className="pause-timer-text">
                {(() => {
                  const remainingMs = Math.max(0, MAX_PAUSE_TIME - totalPauseTime - currentPauseElapsed);
                  const minutes = Math.floor(remainingMs / 60000);
                  const seconds = Math.floor((remainingMs % 60000) / 1000);
                  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                })()}
              </span>
            </div>
          )}

          {/* Audio Quality Hint - TO DISABLE: Comment out this block */}
          {audioQualityHint && interviewState.listening && (
            <div className="audio-quality-hint">
              {audioQualityHint}
            </div>
          )}

          {/* Controls Bar */}
          <div className="controls-bar">
            <button
              className={`control-btn ${interviewState.micMuted ? 'danger' : 'secondary'} ${interviewState.listening ? 'active' : ''}`}
              title={
                showProcessing ? 'Processing your response...' :
                  aiCurrentlySpeaking ? 'Please wait for AI to finish speaking' :
                    (interviewState.micMuted ? 'Interview Paused - Click to Resume' : 'Microphone')
              }
              onClick={toggleMicrophone}
              disabled={aiCurrentlySpeaking || showProcessing}
              style={{
                opacity: (aiCurrentlySpeaking || showProcessing) ? 0.5 : 1,
                cursor: (aiCurrentlySpeaking || showProcessing) ? 'not-allowed' : 'pointer'
              }}
            >
              {interviewState.micMuted ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              )}
            </button>

            {/* Silence Indicator - Clickable for manual submission in noisy environments */}
            {showSilenceIndicator && (
              <div className="silence-indicator">
                <div
                  className="silence-circle silence-circle-clickable"
                  style={{ '--progress': `${silenceProgress * 100}%` } as React.CSSProperties}
                  onClick={() => {
                    if (interviewState.active && interviewState.listening) {
                      console.log('🖱️ Manual submit via silence indicator click');
                      stopBrowserRecording();
                    }
                  }}
                  title="Click to submit your answer"
                >
                  <div className="silence-text">{silenceText}</div>
                </div>
              </div>
            )}

            <button
              className="control-btn danger"
              title="End Interview"
              onClick={endInterview}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                <line x1="23" y1="1" x2="17" y2="7" />
                <line x1="17" y1="1" x2="23" y2="7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Chat Sidebar */}
        <div className="chat-sidebar">
          <div className="chat-header">
            <div className="chat-title">
              Interview Chat
            </div>
            <div className="question-counter">
              Question {interviewState.questionCount} of {interviewState.maxQuestions}
            </div>
          </div>

          {/* Audio Waveform Visualization */}
          {interviewState.listening && (
            <div className={`audio-waveform ${waveformActive ? 'active' : ''}`}>
              <div className="waveform-label">
                Voice Activity {waveformActive ? '(Active)' : '(Listening)'}
              </div>
              <div className="waveform-container">
                {audioLevels.map((level, index) => {
                  // Calculate decibel level for this bar
                  const barLevelDB = level > 0 ? 20 * Math.log10(level) : -100;
                  const isHighDB = barLevelDB >= -6.0;
                  const barHeight = Math.max(level * 100, 3);

                  return (
                    <div
                      key={index}
                      className={`waveform-bar ${isHighDB ? 'high-db' : 'low-db'}`}
                      style={{
                        height: `${barHeight}%`,
                        opacity: waveformActive
                          ? Math.max(0.6, 1 - (audioLevels.length - index) * 0.03)
                          : 0.4 + (level * 0.6),
                        transform: waveformActive && level > 0.3
                          ? `scaleY(${1 + level * 0.2})`
                          : 'scaleY(1)',
                        transformOrigin: 'center'
                      }}
                    />
                  );
                })}
              </div>
              <div
                className="voice-level-indicator"
                data-db={decibelLevel.toFixed(1)}
              >
                <span>Voice Level: {(currentVoiceLevel * 100).toFixed(0)}%</span>
              </div>
            </div>
          )}

          {/* Chat Messages */}
          <div className="chat-messages-panel" ref={chatMessagesPanelRef}>
          <div className="chat-messages" ref={chatMessagesScrollRef}>
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`message ${message.sender} ${
                  index === messages.length - 1 ? 'message-recent' : 'message-past'
                }`}
              >
                <div className="message-bubble">
                  {message.sender === 'ai' ? (
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  ) : (
                    message.text
                  )}
                </div>
                <div className="message-time">{message.timestamp}</div>
              </div>
            ))}
            {/* Auto-scroll anchor */}
            <div ref={chatMessagesEndRef} />
          </div>
          </div>

          {/* Status Bar */}
          <div className="status-bar">
            <div className="status-dot"></div>
            <div>{stripEmojis(status)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIInterviewInterface;
